import { getCSRFToken } from '../utils/getCSRFToken';
import Pica from 'pica';

export default function registerFullBookingStore() {
  Alpine.store('fullBookingStore', {
    isLoading: true,
    isEditing: false,
    toastStore: null, // Will be initialized after Alpine is ready
    bookingData: {
      calendar: '',
      booking_id: '',
      main_guest: {
        id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        address: '',
        country: '',
        country_name: '',
        nationality: '',
        nationality_name: '',
        date_of_birth: '',
        document_type: '',
        document_number: '',
        document_issue_date: '',
        document_issuing_country: '',
        document_issuing_country_name: '',
        document_expiration_date: '',
        custom_fields: {},
      },
      additional_guests: [],
      custom_field_definitions: [],
      booking_items: [],
      booking_extras: [],
      aggregated_breakdown: {
        base_price: '',
        net_base_price: '',
        final_price: '',
        taxes_and_fees: {},
      },
      transactions: [],
      amount_due: 0,
      total_with_adjustments: 0,
      booking_number: '',
      created_at: '',
      created_by: {
        email: '',
      },
      status: '',
      pending_expires_at: null,
      checkin_status: '',
      human_readable_status: '',
      total_price: 0,
      total_transactions: 0,
      total_taxes_and_fees: 0,
      extras: [],
      digital_waiver: null,
      hostkit_enabled: false,
      guest_allocation: {
        required: false,
        required_for_hostkit: false,
        is_ready: true,
        is_ready_for_hostkit: true,
        missing_booking_item_ids: [],
        booking_items: [],
        allocations: [],
      },
    },
    transactionFormPaymentMethod: '', // Reset to '' after adding a transaction
    countries: [],
    notes: [],
    bookingFiles: [],
    newNoteContent: '',
    pendingBookingFile: null,
    uploadingBookingFile: false,
    error: null,
    originalGuestData: {},
    isEmailValid: true,
    // Guest editing state
    isEditingGuest: false,
    editingGuestId: null,
    guestAllocationRowsByGuest: {},
    guestAllocationPickerValueByGuest: {},
    bookingItemGuestPickerValueByItem: {},
    newGuest: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      address: '',
      country: '',
      nationality: '',
      date_of_birth: '',
      document_type: '',
      document_number: '',
      document_issue_date: '',
      document_issuing_country: '',
      document_expiration_date: '',
      custom_fields: {},
    },
    showExtrasModal: false,
    showCreateExtraModal: false,
    newExtra: {
      name: '',
      description: '',
      unit_price: 0,
      image: null,
      imagePreview: null,
      tax_fees: [],
      published: false,
      dropdown_field_title: '',
      dropdown_options_text: '',
    },
    availableExtras: [],
    imageProcessing: false,
    newTaxFee: {
      is_tax: true,
      name: '',
      amount: 0,
      type: 'PERCENTAGE',
      is_inclusive: false,
      applies_to_fees: false,
    },
    showNewTaxFeeModal: false,
    availableTaxes: [],
    availableFees: [],
    currency: 'EUR',
    showEditExtraModal: false,
    editingExtra: {
      id: null,
      name: '',
      description: '',
      unit_price: 0,
      image: null,
      imagePreview: null,
      tax_fees: [],
      published: false,
    },
    taxFeeModalSource: null,
    showDuplicateEmailModal: false,
    duplicateCustomerData: null,
    currentCustomerDataForMerge: null,
    showPaymentRequestModal: false,
    paymentRequest: {
      amount: null,
      dueDate: null,
      note: '',
      cancelIfPendingOnExpiry: false,
      sendReminder: false,
      reminderDaysBeforeDue: 1,
      isSubmitting: false,
    },
    pendingExpirationDraft: '',
    updatingPendingExpiration: false,
    showInvoiceModal: false,
    invoiceData: {
      customer_business_name: '',
      customer_vat_number: '',
      invoice_notes: '',
      isGenerating: false,
      // Partner invoicing
      is_partner_invoice: false,
      partner_name: '',
    },
    // Invoice staleness tracking
    invoiceStaleness: {
      hasIssuedInvoice: false,
      issuedInvoiceNumber: '',
      hasStaleInvoice: false,
      staleInvoiceId: null,
      isChecking: false,
    },
    // InvoiceXpress integration state
    invoiceXpress: {
      invoiceType: null,
      enabled: false,
      documents: [],
      isLoadingDocuments: false,
      isCreatingDraft: false,
      isIssuingInvoice: false,
      isCreatingCreditNote: false,
      isReissuingInvoice: false,
      isUpdatingClient: false,
      isVoidingInvoice: false,
      creditNoteReason: '',
      reissueReason: '',
      voidReason: '',
      showCreditNoteModal: false,
      showReissueModal: false,
      showEditClientModal: false,
      showVoidInvoiceModal: false,
      selectedDocumentId: null,
      editingDocumentId: null,
      voidingDocumentId: null,
      hasCheckedEnabled: false,
      // Client data for invoice
      clientName: '',
      clientEmail: '',
      clientFiscalId: '',
      clientAddress: '',
      clientCountry: '',
      // Partner invoicing
      isPartnerInvoice: false,
      partnerName: '',
      // Store original data for toggling
      _partnerClientData: null,
      _customerClientData: null,
      // Flag to show create form for the other invoice type after one is issued
      showCreateFormForRemainingInvoice: false,
    },
    showWaiverModal: false,
    showChangeLogModal: false,
    showSendEmailModal: false,
    emailTemplates: [],
    emailHistory: [],
    selectedTemplateId: '',
    sendingTemplateEmail: false,
    loadingEmailHistory: false,
    loadingBookingActivity: false,
    waiverData: {
      isGenerating: false,
      isSendingDigitalWaiver: false,
    },
    userPermission: '',

    init() {
      // Initialize toastStore reference
      this.toastStore = Alpine.store('toastStore');

      const fullBookingContainer = document.querySelector(
        '.full-booking-container',
      );
      if (fullBookingContainer) {
        this.userPermission = fullBookingContainer.getAttribute(
          'data-user-permission',
        );
        console.log('userPermission:', this.userPermission);
      }
    },

    getHostkitMissingGuestFields(guest, options = {}) {
      if (!guest) return [];

      const requireEmail = !!options.requireEmail;
      const trim = (value) => (value || '').toString().trim();
      const hasNationality = !!trim(guest.nationality);
      const hasResidenceCountry =
        !!trim(guest.country) || !!trim(guest.document_issuing_country);

      const missing = [];
      if (!trim(guest.first_name)) missing.push('first_name');
      if (!trim(guest.last_name)) missing.push('last_name');
      if (requireEmail && !trim(guest.email)) missing.push('email');
      if (!hasNationality) missing.push('nationality');
      if (!trim(guest.date_of_birth)) missing.push('date_of_birth');
      if (!trim(guest.document_number)) missing.push('document_number');
      if (!trim(guest.document_type)) missing.push('document_type');
      if (!trim(guest.document_issuing_country))
        missing.push('document_issuing_country');
      if (!hasResidenceCountry) missing.push('country_residence');
      return missing;
    },

    getHostkitMissingGuestFieldLabels(guest, options = {}) {
      const labels = {
        first_name: 'First name',
        last_name: 'Last name',
        email: 'Email',
        nationality: 'Nationality',
        date_of_birth: 'Date of birth',
        document_number: 'Document number',
        document_type: 'Document type',
        document_issuing_country: 'Document issuing country',
        country_residence: 'Country of residence',
      };
      return this.getHostkitMissingGuestFields(guest, options).map(
        (field) => labels[field] || field,
      );
    },

    getHostkitReadinessLabelsForGuest(guest, options = {}) {
      return [
        ...this.getHostkitMissingGuestFieldLabels(guest, options),
        ...this.getGuestAllocationReadinessLabelsForGuest(guest),
      ];
    },

    getGuestAllocationState() {
      const fallback = {
        required: false,
        required_for_hostkit: false,
        is_ready: true,
        is_ready_for_hostkit: true,
        missing_booking_item_ids: [],
        booking_items: [],
        allocations: [],
      };
      return this.bookingData.guest_allocation || fallback;
    },

    getGuestAllocationMissingItems() {
      const allocation = this.getGuestAllocationState();
      const missing = new Set(allocation.missing_booking_item_ids || []);
      return (allocation.booking_items || [])
        .filter((item) => missing.has(item.id))
        .map((item) => ({
          id: String(item.id || ''),
          label: item.accommodation_name || `Item ${item.id}`,
        }))
        .filter((item) => item.id);
    },

    getGuestAllocationOptions() {
      const allocation = this.getGuestAllocationState();
      return (allocation.booking_items || []).map((item) => ({
        id: String(item.id || ''),
        accommodation_name: item.accommodation_name || `Item ${item.id}`,
        start_date: item.start_date || '',
        end_date: item.end_date || '',
      }));
    },

    getGuestAllocationOptionLabel(item) {
      if (!item) return '';
      const dateRange =
        item.start_date && item.end_date
          ? ` (${item.start_date} -> ${item.end_date})`
          : '';
      return `${item.accommodation_name || 'Item'}${dateRange}`;
    },

    getUnallocatedBookingItemsForGuest(customerId) {
      const guestKey = String(customerId || '');
      const allocatedItems = new Set(
        this.getGuestAllocatedBookingItems(guestKey).map((item) =>
          String(item.id),
        ),
      );
      return this.getGuestAllocationOptions().filter(
        (item) => !allocatedItems.has(String(item.id)),
      );
    },

    getGuestAllocationPickerValue(customerId) {
      const guestKey = String(customerId || '');
      return this.guestAllocationPickerValueByGuest[guestKey] || '';
    },

    setGuestAllocationPickerValue(customerId, bookingItemId) {
      const guestKey = String(customerId || '');
      if (!guestKey) return;
      this.guestAllocationPickerValueByGuest = {
        ...this.guestAllocationPickerValueByGuest,
        [guestKey]: String(bookingItemId || ''),
      };
    },

    addGuestAllocationFromPicker(customerId) {
      const guestKey = String(customerId || '');
      if (!guestKey) return;
      const selectedItemId = this.getGuestAllocationPickerValue(guestKey);
      if (!selectedItemId) return;
      const allocation = this.getGuestAllocationState();
      const alreadyAssigned = (allocation.allocations || []).some(
        (entry) =>
          String(entry.customer_id) === guestKey &&
          String(entry.booking_item_id) === String(selectedItemId),
      );
      if (!alreadyAssigned) {
        this.bookingData.guest_allocation = {
          ...allocation,
          allocations: [
            ...(allocation.allocations || []),
            {
              customer_id: guestKey,
              booking_item_id: String(selectedItemId),
            },
          ],
        };
      }
      this.guestAllocationPickerValueByGuest = {
        ...this.guestAllocationPickerValueByGuest,
        [guestKey]: '',
      };
    },

    getBookingGuests() {
      return [
        this.bookingData.main_guest,
        ...(Array.isArray(this.bookingData.additional_guests)
          ? this.bookingData.additional_guests
          : []),
      ].filter((guest) => guest && guest.id);
    },

    getGuestDisplayNameById(customerId) {
      const guest = this.getBookingGuests().find(
        (candidate) => String(candidate.id) === String(customerId),
      );
      if (!guest) return 'Guest';
      return (
        `${guest.first_name || ''} ${guest.last_name || ''}`.trim() || 'Guest'
      );
    },

    getAllocatedGuestIdsForBookingItem(bookingItemId) {
      const allocation = this.getGuestAllocationState();
      const itemKey = String(bookingItemId || '');
      if (!itemKey) return [];
      const ids = [];
      (allocation.allocations || []).forEach((entry) => {
        if (String(entry.booking_item_id) !== itemKey) return;
        const guestId = String(entry.customer_id || '');
        if (!guestId || ids.includes(guestId)) return;
        ids.push(guestId);
      });
      return ids;
    },

    getAllocatedGuestIdForBookingItem(bookingItemId) {
      return this.getAllocatedGuestIdsForBookingItem(bookingItemId)[0] || '';
    },

    getAllocatedGuestsForBookingItem(bookingItemId) {
      return this.getAllocatedGuestIdsForBookingItem(bookingItemId).map(
        (guestId) => ({
          id: guestId,
          name: this.getGuestDisplayNameById(guestId),
        }),
      );
    },

    getAllocatedGuestSummaryForBookingItem(bookingItemId) {
      const guests = this.getAllocatedGuestsForBookingItem(bookingItemId);
      if (!guests.length) return '';
      const firstName = guests[0]?.name || 'Guest';
      if (guests.length === 1) return `(${firstName})`;
      return `(${firstName} +${guests.length - 1})`;
    },

    getAllocatedGuestTooltipForBookingItem(bookingItemId) {
      const guests = this.getAllocatedGuestsForBookingItem(bookingItemId);
      return guests.map((guest) => guest.name || 'Guest').join(', ');
    },

    getUnallocatedGuestsForBookingItem(bookingItemId) {
      const allocated = new Set(
        this.getAllocatedGuestIdsForBookingItem(bookingItemId),
      );
      return this.getBookingGuests().filter(
        (guest) => !allocated.has(String(guest.id)),
      );
    },

    getBookingItemGuestPickerValue(bookingItemId) {
      const itemKey = String(bookingItemId || '');
      return this.bookingItemGuestPickerValueByItem[itemKey] || '';
    },

    setBookingItemGuestPickerValue(bookingItemId, customerId) {
      const itemKey = String(bookingItemId || '');
      if (!itemKey) return;
      this.bookingItemGuestPickerValueByItem = {
        ...this.bookingItemGuestPickerValueByItem,
        [itemKey]: String(customerId || ''),
      };
    },

    async addBookingItemGuestAllocationFromPicker(bookingItemId) {
      const itemKey = String(bookingItemId || '');
      if (!itemKey) return;
      const guestId = this.getBookingItemGuestPickerValue(itemKey);
      if (!guestId) return;
      const allocation = this.getGuestAllocationState();
      const alreadyAssigned = (allocation.allocations || []).some(
        (entry) =>
          String(entry.booking_item_id) === itemKey &&
          String(entry.customer_id) === String(guestId),
      );
      if (!alreadyAssigned) {
        this.bookingData.guest_allocation = {
          ...allocation,
          allocations: [
            ...(allocation.allocations || []),
            {
              customer_id: String(guestId),
              booking_item_id: itemKey,
            },
          ],
        };
        await this.saveGuestAllocations({ showToast: false });
      }
      this.bookingItemGuestPickerValueByItem = {
        ...this.bookingItemGuestPickerValueByItem,
        [itemKey]: '',
      };
    },

    getAllocatedGuestNameForBookingItem(bookingItemId) {
      const guestId = this.getAllocatedGuestIdForBookingItem(bookingItemId);
      return guestId ? this.getGuestDisplayNameById(guestId) : '';
    },

    async setBookingItemGuestAllocation(bookingItemId, customerId) {
      const itemId = String(bookingItemId || '');
      if (!itemId) return;
      const guestId = String(customerId || '').trim();
      const allocation = this.getGuestAllocationState();
      const nextAllocations = (allocation.allocations || []).filter(
        (entry) => String(entry.booking_item_id) !== itemId,
      );
      if (guestId) {
        nextAllocations.push({
          customer_id: guestId,
          booking_item_id: itemId,
        });
      }
      this.bookingData.guest_allocation = {
        ...allocation,
        allocations: nextAllocations,
      };
      this.guestAllocationRowsByGuest = {};
      await this.saveGuestAllocations({ showToast: false });
    },

    async removeBookingItemGuestAllocation(bookingItemId, customerId = '') {
      const itemId = String(bookingItemId || '');
      if (!itemId) return;
      const guestId = String(customerId || '').trim();
      const allocation = this.getGuestAllocationState();
      const nextAllocations = (allocation.allocations || []).filter((entry) => {
        if (String(entry.booking_item_id) !== itemId) return true;
        if (!guestId) return false;
        return String(entry.customer_id) !== guestId;
      });
      this.bookingData.guest_allocation = {
        ...allocation,
        allocations: nextAllocations,
      };
      this.guestAllocationRowsByGuest = {};
      await this.saveGuestAllocations({ showToast: false });
    },

    getGuestAllocatedBookingItems(customerId) {
      const guestKey = String(customerId || '');
      if (!guestKey) return [];
      const allocation = this.getGuestAllocationState();
      const allocatedItemIds = (allocation.allocations || [])
        .filter((entry) => String(entry.customer_id) === guestKey)
        .map((entry) => String(entry.booking_item_id));
      const options = this.getGuestAllocationOptions();
      return options.filter((item) =>
        allocatedItemIds.includes(String(item.id)),
      );
    },

    removeGuestAllocationByItem(customerId, bookingItemId) {
      const guestKey = String(customerId || '');
      const itemKey = String(bookingItemId || '');
      if (!guestKey || !itemKey) return;
      const allocation = this.getGuestAllocationState();
      this.bookingData.guest_allocation = {
        ...allocation,
        allocations: (allocation.allocations || []).filter(
          (entry) =>
            !(
              String(entry.customer_id) === guestKey &&
              String(entry.booking_item_id) === itemKey
            ),
        ),
      };
      this.guestAllocationRowsByGuest = {};
    },

    getGuestAllocationReadinessLabelsForGuest(guest) {
      if (!guest?.id) return [];
      const allocation = this.getGuestAllocationState();
      if (!allocation.required_for_hostkit) return [];
      const hasAnyAllocation = (allocation.allocations || []).some(
        (entry) => String(entry.customer_id) === String(guest.id),
      );
      return hasAnyAllocation ? [] : ['Not allocated to a booking item'];
    },

    getGuestAllocationRowValues(customerId) {
      const guestKey = String(customerId || '');
      if (!guestKey) return [''];
      const cachedRows = this.guestAllocationRowsByGuest[guestKey];
      if (Array.isArray(cachedRows) && cachedRows.length > 0) {
        return cachedRows;
      }
      const allocation = this.getGuestAllocationState();
      const selected = [];
      (allocation.allocations || []).forEach((entry) => {
        if (String(entry.customer_id) !== guestKey) return;
        const itemId = String(entry.booking_item_id || '');
        if (!itemId || selected.includes(itemId)) return;
        selected.push(itemId);
      });
      const rows = selected.length ? selected : [''];
      this.guestAllocationRowsByGuest = {
        ...this.guestAllocationRowsByGuest,
        [guestKey]: rows,
      };
      return rows;
    },

    _syncGuestAllocationRowsToState(customerId) {
      const guestKey = String(customerId || '');
      if (!guestKey) return;
      const rows = this.getGuestAllocationRowValues(guestKey);
      const normalizedItemIds = [];
      rows.forEach((rawItemId) => {
        const itemId = String(rawItemId || '').trim();
        if (!itemId || normalizedItemIds.includes(itemId)) return;
        normalizedItemIds.push(itemId);
      });
      const allocation = this.getGuestAllocationState();
      const nextAllocations = (allocation.allocations || []).filter(
        (entry) => String(entry.customer_id) !== guestKey,
      );
      normalizedItemIds.forEach((itemId) => {
        nextAllocations.push({
          customer_id: guestKey,
          booking_item_id: itemId,
        });
      });
      this.bookingData.guest_allocation = {
        ...allocation,
        allocations: nextAllocations,
      };
    },

    setGuestAllocationRowValue(customerId, rowIndex, bookingItemId) {
      const guestKey = String(customerId || '');
      if (!guestKey) return;
      const rows = [...this.getGuestAllocationRowValues(guestKey)];
      const index = Math.max(0, Number(rowIndex) || 0);
      while (rows.length <= index) rows.push('');
      rows[index] = String(bookingItemId || '');
      this.guestAllocationRowsByGuest = {
        ...this.guestAllocationRowsByGuest,
        [guestKey]: rows,
      };
      this._syncGuestAllocationRowsToState(guestKey);
    },

    addGuestAllocationRow(customerId) {
      const guestKey = String(customerId || '');
      if (!guestKey) return;
      const rows = [...this.getGuestAllocationRowValues(guestKey), ''];
      this.guestAllocationRowsByGuest = {
        ...this.guestAllocationRowsByGuest,
        [guestKey]: rows,
      };
    },

    removeGuestAllocationRow(customerId, rowIndex) {
      const guestKey = String(customerId || '');
      if (!guestKey) return;
      const rows = [...this.getGuestAllocationRowValues(guestKey)];
      const index = Math.max(0, Number(rowIndex) || 0);
      if (index >= 0 && index < rows.length) {
        rows.splice(index, 1);
      }
      if (!rows.length) rows.push('');
      this.guestAllocationRowsByGuest = {
        ...this.guestAllocationRowsByGuest,
        [guestKey]: rows,
      };
      this._syncGuestAllocationRowsToState(guestKey);
    },

    isGuestAllocatedToItem(customerId, bookingItemId) {
      const allocation = this.getGuestAllocationState();
      return (allocation.allocations || []).some(
        (entry) =>
          String(entry.customer_id) === String(customerId) &&
          String(entry.booking_item_id) === String(bookingItemId),
      );
    },

    toggleGuestItemAllocation(customerId, bookingItemId, checked) {
      const allocation = this.getGuestAllocationState();
      const nextAllocations = (allocation.allocations || []).filter(
        (entry) =>
          !(
            String(entry.customer_id) === String(customerId) &&
            String(entry.booking_item_id) === String(bookingItemId)
          ),
      );
      if (checked) {
        nextAllocations.push({
          customer_id: String(customerId),
          booking_item_id: String(bookingItemId),
        });
      }
      this.bookingData.guest_allocation = {
        ...allocation,
        allocations: nextAllocations,
      };
    },

    async fetchGuestAllocations() {
      if (!this.bookingData.booking_id) return;
      const calendarId = this.bookingData.calendar_id;
      if (!calendarId) return;
      try {
        const response = await fetch(
          `/hostkit/api/guest-allocations/?calendar_id=${calendarId}&booking_id=${this.bookingData.booking_id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Unable to load guest allocations');
        }
        this.bookingData.guest_allocation = {
          ...this.getGuestAllocationState(),
          required: !!data.allocation_readiness?.required,
          required_for_hostkit:
            !!data.allocation_readiness?.required_for_hostkit,
          is_ready: !!data.allocation_readiness?.is_ready,
          is_ready_for_hostkit:
            data.allocation_readiness?.is_ready_for_hostkit !== undefined
              ? !!data.allocation_readiness?.is_ready_for_hostkit
              : true,
          missing_booking_item_ids:
            data.allocation_readiness?.missing_booking_item_ids || [],
          booking_items: Array.isArray(data.booking_items)
            ? data.booking_items
            : [],
          allocations: Array.isArray(data.allocations) ? data.allocations : [],
        };
        this.guestAllocationRowsByGuest = {};
        this.guestAllocationPickerValueByGuest = {};
        this.bookingItemGuestPickerValueByItem = {};
      } catch (error) {
        console.error('Failed to fetch guest allocations:', error);
      }
    },

    async saveGuestAllocations(options = {}) {
      const { showToast = true } = options;
      if (!this.bookingData.booking_id) return;
      const calendarId = this.bookingData.calendar_id;
      if (!calendarId) return;
      const toastStore = Alpine.store('toastStore');
      const allocation = this.getGuestAllocationState();
      try {
        const response = await fetch('/hostkit/api/guest-allocations/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            calendar_id: calendarId,
            booking_id: this.bookingData.booking_id,
            allocations: allocation.allocations || [],
          }),
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Unable to save guest allocations');
        }
        await this.fetchGuestAllocations();
        if (showToast) {
          toastStore.addToast('Guest allocation saved', 'success');
        }
        return true;
      } catch (error) {
        if (showToast) {
          toastStore.addToast(
            error.message || 'Failed to save guest allocation',
            'danger',
          );
        }
        return false;
      }
    },

    async fetchBookingData(bookingId) {
      const url = `/fullbooking/load-booking-data/${bookingId}/`;
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        // Ensure the booking_id is retained
        this.bookingData = { ...data, booking_id: bookingId };
        const allocationSource = data.guest_allocation || {};
        this.bookingData.guest_allocation = {
          required: !!allocationSource.required,
          required_for_hostkit: !!allocationSource.required_for_hostkit,
          is_ready: allocationSource ? !!allocationSource.is_ready : true,
          is_ready_for_hostkit: allocationSource
            ? !!allocationSource.is_ready_for_hostkit
            : true,
          missing_booking_item_ids:
            allocationSource.missing_booking_item_ids || [],
          booking_items: Array.isArray(allocationSource.booking_items)
            ? allocationSource.booking_items
            : [],
          allocations: Array.isArray(allocationSource.allocations)
            ? allocationSource.allocations
            : [],
        };
        this.guestAllocationRowsByGuest = {};
        this.guestAllocationPickerValueByGuest = {};
        this.bookingItemGuestPickerValueByItem = {};

        // Ensure custom_fields are properly initialized as reactive objects
        if (
          this.bookingData.main_guest &&
          this.bookingData.main_guest.custom_fields
        ) {
          this.bookingData.main_guest.custom_fields = {
            ...this.bookingData.main_guest.custom_fields,
          };
        }

        // Ensure additional guests custom_fields are properly initialized
        if (this.bookingData.additional_guests) {
          this.bookingData.additional_guests =
            this.bookingData.additional_guests.map((guest) => ({
              ...guest,
              custom_fields: guest.custom_fields
                ? { ...guest.custom_fields }
                : {},
            }));
        }

        // Ensure custom_field_definitions are properly initialized as reactive array
        if (this.bookingData.custom_field_definitions) {
          this.bookingData.custom_field_definitions = [
            ...this.bookingData.custom_field_definitions,
          ];
        }

        // Ensure change_log is properly initialized as reactive array
        if (this.bookingData.change_log) {
          this.bookingData.change_log = [
            ...Array.from(this.bookingData.change_log),
          ];
        } else {
          this.bookingData.change_log = [];
        }

        // Required for country/nationality selects across guest forms.
        if (!this.countries.length) {
          await this.fetchCountries();
        }

        this.initializePaymentRequest();
        this.syncPendingExpirationDraft();

        // Check for invoice staleness after loading booking data
        this.checkInvoiceStaleness();
        this.fetchEmailTemplates();
        this.refreshBookingActivityData();

        console.log('bookingData:', this.bookingData);
        console.log('change_log:', this.bookingData.change_log);
      } catch (error) {
        console.error('Failed to fetch booking data:', error);
        this.error = error.message;
      } finally {
        this.isLoading = false;
      }
    },

    async checkInvoiceStaleness() {
      // Reset state first - ensure hasIssuedInvoice is false
      this.invoiceStaleness.hasIssuedInvoice = false;
      this.invoiceStaleness.issuedInvoiceNumber = '';
      this.invoiceStaleness.hasStaleInvoice = false;
      this.invoiceStaleness.staleInvoiceId = null;
      this.invoiceStaleness.isChecking = true;

      try {
        const bookingId = this.bookingData?.booking_id;
        if (!bookingId) {
          console.log('checkInvoiceStaleness: No booking ID');
          this.invoiceStaleness.isChecking = false;
          return;
        }

        // Fetch documents for this booking
        const docsResponse = await fetch(
          `/invoicexpress/documents/${bookingId}/`,
        );
        if (!docsResponse.ok) {
          console.log('checkInvoiceStaleness: API error', docsResponse.status);
          this.invoiceStaleness.isChecking = false;
          return;
        }

        const docsData = await docsResponse.json();
        const documents = docsData.documents || [];
        console.log(
          'checkInvoiceStaleness: Found',
          documents.length,
          'documents',
        );

        // Find an issued invoice (not credit note, not draft)
        const issuedInvoice = documents.find(
          (d) =>
            d.is_issued === true &&
            d.status === 'issued' &&
            ['invoice', 'draft_invoice'].includes(d.document_type),
        );

        if (issuedInvoice && issuedInvoice.invoice_number) {
          console.log(
            'checkInvoiceStaleness: Found issued invoice',
            issuedInvoice.invoice_number,
          );
          this.invoiceStaleness.hasIssuedInvoice = true;
          this.invoiceStaleness.issuedInvoiceNumber =
            issuedInvoice.invoice_number;

          // Check if this invoice is stale (booking has changed)
          if (issuedInvoice.has_booking_changed) {
            this.invoiceStaleness.hasStaleInvoice = true;
            this.invoiceStaleness.staleInvoiceId = issuedInvoice.id;
          }
        } else {
          console.log('checkInvoiceStaleness: No issued invoice found');
        }
      } catch (error) {
        console.error('Error checking invoice staleness:', error);
      } finally {
        this.invoiceStaleness.isChecking = false;
        console.log(
          'checkInvoiceStaleness: Final state',
          JSON.stringify(this.invoiceStaleness),
        );
      }
    },
    async refreshBookingActivityData() {
      this.loadingBookingActivity = true;
      try {
        await Promise.all([
          this.fetchEmailHistory(),
          this.ensureInvoiceDocumentsLoaded(),
        ]);
      } finally {
        this.loadingBookingActivity = false;
      }
    },

    async ensureInvoiceDocumentsLoaded() {
      if (!this.bookingData?.booking_id) return;
      await this.checkInvoiceXpressEnabled();
      if (this.invoiceXpress.enabled) {
        await this.loadInvoiceXpressDocuments();
      } else {
        this.invoiceXpress.documents = [];
      }
    },
    async fetchCartData(booking) {
      const url = `/fullbooking/load-cart/${booking}/`;
      const cartStore = Alpine.store('cartStore');
      try {
        const response = await fetch(url, {
          method: 'GET', // Ensure this matches what the backend expects
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(), // Include CSRF token if needed for security reasons
          },
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        // Log the fetched data for debugging purposes
        console.log('Fetched cart data:', data);

        // Update the cartStore with fetched data
        cartStore.editModeBooking = data.edit_mode_booking;
        cartStore.bookingItemsCount = data.booking_items_count;

        // Optionally, log the updated store values to verify correct updates
        console.log('FB editModeBooking:', cartStore.editModeBooking);
        console.log('FB bookingItemsCount:', cartStore.bookingItemsCount);
      } catch (error) {
        console.error('Failed to fetch cart data:', error);
        this.error = error.message; // Store the error message in the store for UI feedback
      }
    },
    async fetchNotes(bookingId) {
      try {
        const response = await fetch(
          `/fullbooking/fetch_booking_notes/${bookingId}/`,
        );
        const data = await response.json();
        this.notes = data.notes;
        console.log('notes:', this.notes);
      } catch (error) {
        console.error('Failed to fetch notes:', error);
      }
    },
    async fetchBookingFiles(bookingId) {
      try {
        const response = await fetch(
          `/fullbooking/fetch_booking_files/${bookingId}/`,
        );
        const data = await response.json();
        this.bookingFiles = Array.isArray(data.files) ? data.files : [];
      } catch (error) {
        console.error('Failed to fetch booking files:', error);
      }
    },
    onBookingFileSelected(event) {
      const selectedFile = event?.target?.files?.[0] || null;
      if (!selectedFile) {
        return;
      }
      if ((this.bookingFiles || []).length >= 5) {
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            'Maximum of 5 files per booking reached',
            'danger',
          );
        }
        event.target.value = '';
        return;
      }

      const allowedExtensions = new Set([
        'pdf',
        'png',
        'jpg',
        'jpeg',
        'webp',
        'gif',
        'doc',
        'docx',
        'xls',
        'xlsx',
        'csv',
        'txt',
      ]);
      const extension = (
        selectedFile.name.split('.').pop() || ''
      ).toLowerCase();
      if (!allowedExtensions.has(extension)) {
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast('Unsupported file type', 'danger');
        }
        event.target.value = '';
        return;
      }

      if (selectedFile.size > 8 * 1024 * 1024) {
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast('File exceeds 8MB limit', 'danger');
        }
        event.target.value = '';
        return;
      }

      this.pendingBookingFile = selectedFile;
    },
    clearBookingFileSelection() {
      this.pendingBookingFile = null;
      const input = document.getElementById('bookingFileInput');
      if (input) {
        input.value = '';
      }
    },
    async uploadBookingFile() {
      if (!this.pendingBookingFile || this.uploadingBookingFile) {
        return;
      }
      if ((this.bookingFiles || []).length >= 5) {
        this.clearBookingFileSelection();
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            'Maximum of 5 files per booking reached',
            'danger',
          );
        }
        return;
      }

      const bookingId = this.bookingData.booking_id;
      const formData = new FormData();
      formData.append('file', this.pendingBookingFile);
      this.uploadingBookingFile = true;

      try {
        const response = await fetch(`/fullbooking/add_file/${bookingId}/`, {
          method: 'POST',
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
          body: formData,
        });

        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.message || 'Failed to upload file');
        }

        this.bookingFiles = [data.file, ...this.bookingFiles];
        this.clearBookingFileSelection();
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast('File uploaded', 'success');
        }
      } catch (error) {
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            error.message || 'Failed to upload file',
            'danger',
          );
        }
      } finally {
        this.uploadingBookingFile = false;
      }
    },
    async deleteBookingFile(fileId) {
      if (!confirm('Are you sure you want to delete this file?')) {
        return;
      }

      try {
        const response = await fetch(`/fullbooking/delete_file/${fileId}/`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
        });
        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.message || 'Failed to delete file');
        }
        this.bookingFiles = this.bookingFiles.filter(
          (file) => file.id !== fileId,
        );
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast('File deleted', 'success');
        }
      } catch (error) {
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            error.message || 'Failed to delete file',
            'danger',
          );
        }
      }
    },
    async fetchCountries() {
      try {
        const response = await fetch('/customers/countries/');
        const data = await response.json();
        this.countries = data.countries;
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      }
    },
    async cancelBooking(status, cancellationFee = null) {
      const url = `/bookings_app/${this.bookingData.booking_id}/cancel_booking/`;
      const toastStore = Alpine.store('toastStore');
      const cartStore = Alpine.store('cartStore');

      try {
        const requestBody = { status };
        if (
          cancellationFee !== null &&
          cancellationFee !== undefined &&
          cancellationFee !== ''
        ) {
          requestBody.cancellation_fee = cancellationFee;
        }

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();
        if (!response.ok || data.status === 'error') {
          throw new Error(data.message || 'Network response was not ok');
        }

        this.bookingData.status = data.booking.status;
        this.bookingData.human_readable_status =
          data.booking.human_readable_status;

        // Update the cartStore with fetched data
        cartStore.editModeBooking = data.edit_mode_booking;

        // Reload booking data to show adjustment items
        await this.fetchBookingData(this.bookingData.booking_id);

        toastStore.addToast('Booking cancelled successfully', 'success');
      } catch (error) {
        console.error('Failed to update booking status:', error);
        toastStore.addToast(
          error.message || 'An unexpected error occurred',
          'danger',
        );
      }
    },

    async promptCancellationFee() {
      return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.style.cssText =
          'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;';

        const totalPrice = this.bookingData.total_price || 0;

        modal.innerHTML = `
          <div style="background: white; padding: 2rem; border-radius: 8px; max-width: 500px; width: 90%;">
            <h3 style="margin-top: 0;">Cancel Booking</h3>
            <p style="color: #666;">Original Amount: ${this.formatCurrency(
              totalPrice,
            )}</p>

            <div style="margin: 1.5rem 0;">
              <label style="display: block; margin-bottom: 0.5rem;">
                <input type="radio" name="cancelType" value="full" checked style="margin-right: 0.5rem;">
                Full Refund (no cancellation fee)
              </label>
              <label style="display: block; margin-bottom: 0.5rem;">
                <input type="radio" name="cancelType" value="partial" style="margin-right: 0.5rem;">
                Charge Cancellation Fee
              </label>
            </div>

            <div id="feeInputContainer" style="display: none; margin: 1rem 0;">
              <label style="display: block; margin-bottom: 0.5rem; font-weight: 500;">
                Amount to charge (cancellation fee):
              </label>
              <input
                type="number"
                id="cancellationFeeInput"
                step="0.01"
                min="0"
                max="${totalPrice}"
                placeholder="0.00"
                style="width: 100%; padding: 0.5rem; border: 1px solid #ddd; border-radius: 4px;"
              >
              <small style="color: #666; display: block; margin-top: 0.25rem;">
                Guest will be refunded: <span id="refundAmount">${this.formatCurrency(
                  totalPrice,
                )}</span>
              </small>
            </div>

            <div style="display: flex; gap: 1rem; margin-top: 1.5rem;">
              <button id="confirmCancel" class="btn btn-danger" style="flex: 1;">
                Confirm Cancellation
              </button>
              <button id="cancelModal" class="btn btn-secondary" style="flex: 1;">
                Go Back
              </button>
            </div>
          </div>
        `;

        document.body.appendChild(modal);

        const feeContainer = modal.querySelector('#feeInputContainer');
        const feeInput = modal.querySelector('#cancellationFeeInput');
        const refundAmountSpan = modal.querySelector('#refundAmount');
        const radioButtons = modal.querySelectorAll('input[name="cancelType"]');

        // Handle radio button changes
        radioButtons.forEach((radio) => {
          radio.addEventListener('change', (e) => {
            if (e.target.value === 'partial') {
              feeContainer.style.display = 'block';
              feeInput.focus();
            } else {
              feeContainer.style.display = 'none';
            }
          });
        });

        // Update refund amount as user types
        if (feeInput) {
          feeInput.addEventListener('input', (e) => {
            const fee = parseFloat(e.target.value) || 0;
            const refund = totalPrice - fee;
            refundAmountSpan.textContent = this.formatCurrency(
              refund >= 0 ? refund : 0,
            );
          });
        }

        // Confirm button
        modal.querySelector('#confirmCancel').addEventListener('click', () => {
          const selectedType = modal.querySelector(
            'input[name="cancelType"]:checked',
          ).value;
          let result;

          if (selectedType === 'full') {
            result = undefined; // Full refund
          } else {
            const feeValue = feeInput.value;
            result = feeValue && feeValue !== '' ? feeValue : '0';
          }

          document.body.removeChild(modal);
          resolve(result);
        });

        // Cancel button
        modal.querySelector('#cancelModal').addEventListener('click', () => {
          document.body.removeChild(modal);
          resolve(null); // Null means user cancelled
        });
      });
    },

    async updateCustomerDetails() {
      const toastStore = Alpine.store('toastStore');
      this.isEmailValid = this.isValidEmailOrBlank(
        this.bookingData.main_guest.email,
      );
      if (!this.isEmailValid) {
        toastStore.addToast('Please enter a valid email address', 'danger');
        return;
      }
      const url = `/customers/update/${this.bookingData.main_guest.id}/`;
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(this.bookingData.main_guest),
        });

        const data = await response.json();

        // Check for duplicate email conflict
        if (response.status === 409 && data.status === 'duplicate_email') {
          // Store the duplicate customer data and current customer data
          this.duplicateCustomerData = data.duplicate_customer;
          this.currentCustomerDataForMerge = { ...this.bookingData.main_guest };

          // Show the duplicate email modal
          this.showDuplicateEmailModal = true;
          return;
        }

        if (!response.ok || data.status === 'error') {
          throw new Error(data.message || 'Network response was not ok');
        }

        // Update the bookingData.main_guest with the new data
        this.bookingData.main_guest = {
          ...this.bookingData.main_guest,
          ...data.customer,
        };

        // Ensure custom_fields are properly initialized as reactive objects
        if (this.bookingData.main_guest.custom_fields) {
          this.bookingData.main_guest.custom_fields = {
            ...this.bookingData.main_guest.custom_fields,
          };
        }

        console.log('Customer updated successfully:', data);
        toastStore.addToast(
          'Customer details successfully updated!',
          'success',
        );
        await this.saveGuestAllocations({ showToast: false });
        this.isEditing = false; // Exit edit mode after saving
      } catch (error) {
        console.error('Failed to update customer data:', error);
        this.error = error.message;
        toastStore.addToast(
          error.message || 'An unexpected error occurred',
          'danger',
        );
      }
    },

    async handleMergeCustomers(mergeMode) {
      const url = `/customers/update/${this.bookingData.main_guest.id}/`;
      const toastStore = Alpine.store('toastStore');

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            merge_mode: mergeMode,
            existing_customer_id: this.duplicateCustomerData.id,
            current_customer_data: this.currentCustomerDataForMerge,
          }),
        });

        const data = await response.json();

        if (!response.ok || data.status === 'error') {
          throw new Error(data.message || 'Failed to merge customers');
        }

        // Update the bookingData.main_guest with the merged customer data
        this.bookingData.main_guest = {
          ...this.bookingData.main_guest,
          ...data.customer,
        };

        // Ensure custom_fields are properly initialized as reactive objects
        if (this.bookingData.main_guest.custom_fields) {
          this.bookingData.main_guest.custom_fields = {
            ...this.bookingData.main_guest.custom_fields,
          };
        }

        console.log('Customers merged successfully:', data);
        toastStore.addToast(
          data.message || 'Customer details successfully merged!',
          'success',
        );

        // Close modal and exit edit mode
        this.showDuplicateEmailModal = false;
        this.duplicateCustomerData = null;
        this.currentCustomerDataForMerge = null;
        this.isEditing = false;

        // Refresh booking data to reflect any changes
        await this.fetchBookingData(this.bookingData.id);
      } catch (error) {
        console.error('Failed to merge customers:', error);
        this.error = error.message;
        toastStore.addToast(
          error.message || 'An unexpected error occurred',
          'danger',
        );
      }
    },

    closeDuplicateEmailModal() {
      this.showDuplicateEmailModal = false;
      this.duplicateCustomerData = null;
      this.currentCustomerDataForMerge = null;
    },

    // Guest management methods
    async addGuest() {
      if (!this.newGuest.first_name) {
        alert('Please fill in first name');
        return;
      }

      if (!this.countries.length) {
        await this.fetchCountries();
      }

      const url = `/fullbooking/add-guest/${this.bookingData.booking_id}/`;
      const toastStore = Alpine.store('toastStore');
      if (!this.isValidEmailOrBlank(this.newGuest.email)) {
        toastStore.addToast('Please enter a valid email address', 'danger');
        return;
      }

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(this.newGuest),
        });

        const data = await response.json();
        if (!response.ok || data.status === 'error') {
          throw new Error(data.message || 'Network response was not ok');
        }

        // Update booking data with new guest
        this.bookingData.additional_guests = data.booking.additional_guests.map(
          (guest) => ({
            ...guest,
            custom_fields: guest.custom_fields
              ? { ...guest.custom_fields }
              : {},
          }),
        );

        // Reset new guest form
        this.newGuest = {
          first_name: '',
          last_name: '',
          email: '',
          phone_number: '',
          address: '',
          country: '',
          nationality: '',
          date_of_birth: '',
          document_type: '',
          document_number: '',
          document_issue_date: '',
          document_issuing_country: '',
          document_expiration_date: '',
          custom_fields: {},
        };

        toastStore.addToast('Guest added successfully!', 'success');
        await this.fetchGuestAllocations();
      } catch (error) {
        console.error('Failed to add guest:', error);
        toastStore.addToast(error.message || 'Failed to add guest', 'danger');
      }
    },

    async updateGuest(guestId) {
      const guest = this.bookingData.additional_guests.find(
        (g) => g.id === guestId,
      );
      if (!guest) return;

      const url = `/fullbooking/update-guest/${this.bookingData.booking_id}/${guestId}/`;
      const toastStore = Alpine.store('toastStore');
      if (!this.isValidEmailOrBlank(guest.email)) {
        toastStore.addToast('Please enter a valid email address', 'danger');
        return;
      }

      try {
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(guest),
        });

        const data = await response.json();
        if (!response.ok || data.status === 'error') {
          throw new Error(data.message || 'Network response was not ok');
        }

        // Update booking data
        this.bookingData.additional_guests = data.booking.additional_guests.map(
          (guest) => ({
            ...guest,
            custom_fields: guest.custom_fields
              ? { ...guest.custom_fields }
              : {},
          }),
        );
        this.isEditingGuest = false;
        this.editingGuestId = null;

        toastStore.addToast('Guest updated successfully!', 'success');
        await this.saveGuestAllocations({ showToast: false });
      } catch (error) {
        console.error('Failed to update guest:', error);
        toastStore.addToast(
          error.message || 'Failed to update guest',
          'danger',
        );
      }
    },

    async removeGuest(guestId) {
      if (!confirm('Are you sure you want to remove this guest?')) {
        return;
      }

      const url = `/fullbooking/remove-guest/${this.bookingData.booking_id}/${guestId}/`;
      const toastStore = Alpine.store('toastStore');

      try {
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
        });

        const data = await response.json();
        if (!response.ok || data.status === 'error') {
          throw new Error(data.message || 'Network response was not ok');
        }

        // Update booking data
        this.bookingData.additional_guests = data.booking.additional_guests.map(
          (guest) => ({
            ...guest,
            custom_fields: guest.custom_fields
              ? { ...guest.custom_fields }
              : {},
          }),
        );

        // Switch to the main guest tab after deletion
        const mainGuestTab = document.getElementById('main-guest-tab');
        if (mainGuestTab) {
          mainGuestTab.click();
        }

        toastStore.addToast('Guest removed successfully!', 'success');
        await this.fetchGuestAllocations();
      } catch (error) {
        console.error('Failed to remove guest:', error);
        toastStore.addToast(
          error.message || 'Failed to remove guest',
          'danger',
        );
      }
    },

    startEditingGuest(guestId) {
      this.isEditingGuest = true;
      this.editingGuestId = guestId;
      this.fetchGuestAllocations();
    },

    cancelEditingGuest() {
      this.isEditingGuest = false;
      this.editingGuestId = null;
    },

    async addTransaction() {
      console.log('Transaction Form Clicked!!!');
      const transactionForm = document.querySelector('.transaction-form');
      const submitButton = transactionForm.querySelector(
        'button[type="submit"]',
      );

      // Disable the submit button to prevent multiple submissions
      submitButton.disabled = true;
      const formData = new FormData(transactionForm);

      const typeTransaction = formData.get('type_transaction');
      const partner = formData.get('partner');
      if (!typeTransaction && !partner) {
        this.showErrorMessage('Please select a payment method.');
        setTimeout(this.hideErrorMessage, 5000);
        submitButton.disabled = false;
        return;
      }

      const newTransactionAmount = parseFloat(formData.get('amount'));
      const currentAmountDue = parseFloat(this.bookingData.amount_due);

      // Check if amount is empty or invalid
      if (!newTransactionAmount || isNaN(newTransactionAmount)) {
        this.showErrorMessage('Please enter a valid amount.');
        setTimeout(this.hideErrorMessage, 5000);
        // Re-enable the submit button
        submitButton.disabled = false;
        return;
      }

      if (newTransactionAmount > currentAmountDue) {
        this.showErrorMessage('Payment amount exceeds the amount due.');
        setTimeout(this.hideErrorMessage, 5000);
        // Re-enable the submit button
        submitButton.disabled = false;
        return;
      }

      try {
        const response = await fetch('/payments/add_transaction/', {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRFToken': getCSRFToken(),
          },
        });

        const data = await response.json();
        if (data.status === 'success') {
          this.bookingData.transactions.push(data.transaction);
          this.bookingData.total_transactions = parseFloat(
            data.total_transactions,
          );
          this.bookingData.amount_due = parseFloat(data.amount_due);
          this.bookingData.status = data.booking_status;
          this.bookingData.human_readable_status = data.human_readable_status;
          if (
            this.bookingData.partner &&
            this.bookingData.partner.invoicing_mode === 'PARTNER'
          ) {
            this.transactionFormPaymentMethod =
              'partner_' + this.bookingData.partner.id;
          } else {
            this.transactionFormPaymentMethod = '';
          }
          // Clear only the amount field; do not form.reset() so the payment method select stays at placeholder
          const amountInput = transactionForm.querySelector(
            'input[name="amount"]',
          );
          if (amountInput) amountInput.value = '';
        } else {
          console.error(data.errors);
          this.showErrorMessage('Failed to add transaction.');
          setTimeout(this.hideErrorMessage, 5000);
        }
      } catch (error) {
        console.error('Failed to add transaction:', error);
        this.showErrorMessage('Failed to add transaction.');
        setTimeout(this.hideErrorMessage, 5000);
      } finally {
        // Always re-enable the submit button
        submitButton.disabled = false;
      }
    },
    promptUpdatePartnerCommission(currency) {
      const partner = this.bookingData.partner;
      const commissionRate = partner.commission_rate;
      // Runtime pricing is gross-only, so commission preview base is total_price.
      const baseTotal = this.bookingData.total_price || 0;
      const currentCommission = parseFloat(
        this.bookingData.partner_commission || 0,
      );
      const newCommission = ((baseTotal * commissionRate) / 100).toFixed(2);
      const isManualOverride =
        this.bookingData.partner_commission_manual_override === true;
      const isPartnerAutoUpdate = partner.invoicing_mode === 'PARTNER';
      const isNetSensitiveMode =
        (partner.invoicing_mode === 'PARTNER' &&
          partner.partner_invoice_amount_type === 'FULL_MINUS_COMMISSION') ||
        (partner.invoicing_mode === 'CUSTOMER' &&
          partner.collects_commission === true);
      const hasIssuedInvoiceForBooking =
        this.invoiceStaleness.hasIssuedInvoice ||
        this.hasIssuedInvoiceInDocuments();
      const showIssuedNetWarning =
        isNetSensitiveMode && hasIssuedInvoiceForBooking;

      const modal = document.createElement('div');
      modal.style.cssText =
        'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;';

      const totalLabel =
        baseTotal !== (this.bookingData.total_price || 0)
          ? 'Booking total (public):'
          : 'Current total:';
      modal.innerHTML = `
        <div style="background: white; padding: 2rem; border-radius: 8px; max-width: 560px; width: 90%;">
          <h3 style="margin-top: 0;">Update Partner Commission</h3>
          <p style="color: #666; margin-bottom: 1rem;">
            This will update the partner commission to <strong>${commissionRate}%</strong> of the booking total${
              baseTotal !== (this.bookingData.total_price || 0)
                ? ' (public amount)'
                : ''
            }.
          </p>
          ${
            showIssuedNetWarning
              ? '<div style="background: #fff3cd; border: 1px solid #ffe69c; color: #664d03; border-radius: 4px; padding: 0.75rem; margin-bottom: 1rem;">This booking has an issued net invoice. Commission updates are blocked until the invoice is voided/credited and re-issued.</div>'
              : ''
          }
          <div style="background: #f8f9fa; padding: 1rem; border-radius: 4px; margin: 1rem 0;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem;">
              <span style="color: #666;">${totalLabel}</span>
              <strong>${this.formatCurrency(baseTotal, currency)}</strong>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem;">
              <span style="color: #666;">Current Commission:</span>
              <strong>${this.formatCurrency(
                currentCommission,
                currency,
              )}</strong>
            </div>
            <div style="display: flex; justify-content: space-between; border-top: 1px solid #dee2e6; padding-top: 0.5rem; margin-top: 0.5rem;">
              <span style="color: #666;">New Commission (${commissionRate}%):</span>
              <strong style="color: #28a745;">${this.formatCurrency(
                parseFloat(newCommission),
                currency,
              )}</strong>
            </div>
          </div>
          <div style="margin: 1rem 0;">
            <label for="manualCommissionInput" style="display: block; margin-bottom: 0.5rem; color: #666;">Manual Commission Amount</label>
            <input
              id="manualCommissionInput"
              type="number"
              inputmode="decimal"
              step="0.01"
              min="0"
              value="${currentCommission.toFixed(2)}"
              style="width: 100%; border: 1px solid #ced4da; border-radius: 4px; padding: 0.5rem;"
            />
            ${
              isPartnerAutoUpdate
                ? '<small style="display: block; margin-top: 0.35rem; color: #666;">Saving manual amount pauses automatic commission updates until recalculation.</small>'
                : ''
            }
            ${
              isManualOverride
                ? '<small style="display: block; margin-top: 0.25rem; color: #fd7e14;">Manual override is currently active for this booking.</small>'
                : ''
            }
            <small id="manualCommissionError" style="display: none; margin-top: 0.35rem; color: #dc3545;"></small>
          </div>
          <div style="display: flex; gap: 1rem; margin-top: 1.5rem;">
            <button id="cancelUpdate" class="btn btn-secondary" style="flex: 1;">
              Cancel
            </button>
            <button id="recalculateUpdate" class="btn btn-dark" style="flex: 1;">
              Recalculate
            </button>
            <button id="manualUpdate" class="btn btn-outline-dark" style="flex: 1;">
              Save Manual
            </button>
          </div>
        </div>
      `;

      document.body.appendChild(modal);

      modal.querySelector('#cancelUpdate').addEventListener('click', () => {
        document.body.removeChild(modal);
      });

      modal
        .querySelector('#recalculateUpdate')
        .addEventListener('click', () => {
          document.body.removeChild(modal);
          this.updatePartnerCommission({ mode: 'recalculate' });
        });

      modal.querySelector('#manualUpdate').addEventListener('click', () => {
        const manualInput = modal.querySelector('#manualCommissionInput');
        const errorNode = modal.querySelector('#manualCommissionError');
        const normalizedValue = (manualInput.value || '')
          .trim()
          .replace(',', '.');
        const parsedValue = Number.parseFloat(normalizedValue);

        if (Number.isNaN(parsedValue) || parsedValue < 0) {
          errorNode.textContent = 'Please enter a valid non-negative amount.';
          errorNode.style.display = 'block';
          return;
        }

        errorNode.style.display = 'none';
        document.body.removeChild(modal);
        this.updatePartnerCommission({
          mode: 'manual',
          manualCommission: parsedValue.toFixed(2),
        });
      });

      // Close on backdrop click
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          document.body.removeChild(modal);
        }
      });
    },

    async updatePartnerCommission({
      mode = 'recalculate',
      manualCommission = null,
    } = {}) {
      const bookingId = this.bookingData.booking_id;
      const toastStore = Alpine.store('toastStore');

      if (
        !this.bookingData.partner ||
        !this.bookingData.partner.commission_rate
      ) {
        if (toastStore) {
          toastStore.addToast(
            'Booking does not have a partner with commission rate',
            'error',
          );
        }
        return;
      }

      const url = `/fullbooking/update-partner-commission/${bookingId}/`;
      const payload = { mode };
      if (mode === 'manual') {
        payload.manual_commission = manualCommission;
      }

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (response.ok && data.status === 'success') {
          // Refresh booking data to get updated commission
          await this.fetchBookingData(bookingId);
          if (toastStore) {
            toastStore.addToast(
              mode === 'manual'
                ? 'Partner commission manually updated'
                : 'Partner commission recalculated successfully',
              'success',
            );
          }
        } else {
          if (toastStore) {
            toastStore.addToast(
              data.error || 'Failed to update partner commission',
              'error',
            );
          }
        }
      } catch (error) {
        console.error('Failed to update partner commission:', error);
        if (toastStore) {
          toastStore.addToast('Failed to update partner commission', 'error');
        }
      }
    },

    async updatePartnerBookingCode() {
      const bookingId = this.bookingData.booking_id;
      const partnerBookingCode = this.bookingData.partner_booking_code || '';
      const toastStore = Alpine.store('toastStore');

      const url = `/fullbooking/update-partner-booking-code/${bookingId}/`;
      try {
        const response = await fetch(url, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            partner_booking_code: partnerBookingCode,
          }),
        });

        const data = await response.json();
        if (response.ok && data.status === 'success') {
          this.bookingData.partner_booking_code =
            data.booking.partner_booking_code;
          if (toastStore) {
            toastStore.addToast(
              'Partner booking code updated successfully',
              'success',
            );
          }
        } else {
          // Revert on error
          await this.fetchBookingData(bookingId);
          if (toastStore) {
            toastStore.addToast(
              data.error || 'Failed to update partner booking code',
              'error',
            );
          }
        }
      } catch (error) {
        console.error('Failed to update partner booking code:', error);
        // Revert on error
        await this.fetchBookingData(bookingId);
        if (toastStore) {
          toastStore.addToast('Failed to update partner booking code', 'error');
        }
      }
    },

    async deleteTransaction(transactionId) {
      if (!confirm('Are you sure you want to delete this transaction?')) {
        return;
      }

      const url = `/payments/delete_transaction/${transactionId}/`;
      try {
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        if (data.status === 'success') {
          this.bookingData.transactions = this.bookingData.transactions.filter(
            (transaction) => transaction.id !== transactionId,
          );
          this.bookingData.total_transactions = parseFloat(
            data.total_transactions,
          );
          this.bookingData.amount_due = parseFloat(data.amount_due);
          this.bookingData.status = data.booking_status;
          this.bookingData.human_readable_status = data.human_readable_status;
        } else {
          console.error(data.message);
        }
      } catch (error) {
        console.error('Failed to delete transaction:', error);
      }
    },
    noteHasVisibleContent(html) {
      const temp = document.createElement('div');
      temp.innerHTML = html || '';
      const textContent = (temp.textContent || temp.innerText || '')
        .replace(/\u00A0/g, ' ')
        .trim();
      return textContent.length > 0;
    },
    async addNote() {
      const inputField = document.getElementById('noteContent');
      // Sync from DOM so Enter-created line breaks are included (input event can miss them)
      if (inputField) {
        this.newNoteContent = inputField.innerHTML;
      }
      if (!this.noteHasVisibleContent(this.newNoteContent)) {
        return;
      }

      if (inputField) {
        // Disable the editor to prevent duplicate submissions.
        inputField.setAttribute('contenteditable', 'false');
      }

      const url = `/fullbooking/add_note/${this.bookingData.booking_id}/`;
      const payload = {
        content: this.newNoteContent,
      };

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        if (data.status === 'success') {
          this.notes.push(data.note);
          this.newNoteContent = '';
          if (inputField) {
            inputField.innerHTML = '';
          }
        } else {
          console.error(data.message);
        }
      } catch (error) {
        console.error('Failed to add note:', error);
      } finally {
        if (inputField) {
          inputField.setAttribute('contenteditable', 'true');
        }
      }
    },
    editNote(note) {
      note.isEditing = true;
      note.newContent = note.content_html || note.content || '';
      // Set editor content once when opening (no x-html binding), so typing direction stays correct
      setTimeout(() => {
        const el = document.getElementById(`noteEditContent-${note.id}`);
        if (el) el.innerHTML = note.newContent || '';
      }, 0);
    },

    async saveNoteEdit(note) {
      // Sync from DOM so Enter-created line breaks are included (input event can miss them)
      const editEl = document.getElementById(`noteEditContent-${note.id}`);
      if (editEl) {
        note.newContent = editEl.innerHTML;
      }
      if (!this.noteHasVisibleContent(note.newContent)) {
        return;
      }

      const url = `/fullbooking/edit_note/${note.id}/`;
      const payload = { content: note.newContent };

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        if (data.status === 'success') {
          note.content = data.note.content;
          note.content_html = data.note.content_html || data.note.content;
          note.isEditing = false;
        } else {
          console.error(data.message);
        }
      } catch (error) {
        console.error('Failed to edit note:', error);
      }
    },
    async deleteNote(noteId) {
      if (!confirm('Are you sure you want to delete this note?')) {
        return;
      }

      const url = `/fullbooking/delete_note/${noteId}/`;

      try {
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        if (data.status === 'success') {
          this.notes = this.notes.filter((note) => note.id !== noteId);
        } else {
          console.error(data.message);
        }
      } catch (error) {
        console.error('Failed to delete note:', error);
      }
    },

    async deleteBookingItem(bookingItemId) {
      console.log('bookingItemId:', bookingItemId);
      console.log('bookingId:', this.bookingData.booking_id);
      try {
        const response = await fetch(
          `/bookings_app/booking-item-archive/${bookingItemId}/`,
          {
            method: 'POST',
            headers: {
              'X-CSRFToken': getCSRFToken(),
              'Content-Type': 'application/json',
            },
          },
        );

        const data = await response.json();
        if (data.status === 'success') {
          console.log('Deleted booking item successfully');
          await this.fetchBookingData(this.bookingData.booking_id);
          await this.fetchCartData(this.bookingData.booking_id);
        } else {
          console.error(data.message);
        }
      } catch (error) {
        console.error('Failed to delete booking item:', error);
      }
    },
    startEditing() {
      // Store the original guest data before editing
      this.originalGuestData = { ...this.bookingData.main_guest };
      this.fetchCountries();
      this.isEditing = true;
      this.fetchGuestAllocations();
      // Set the visible date of birth in yyyy-mm-dd format
      if (this.bookingData.main_guest.date_of_birth) {
        const [day, month, year] =
          this.bookingData.main_guest.date_of_birth.split('-');
        this.visible_date_of_birth = `${year}-${month}-${day}`;
      }
    },
    cancelEditing() {
      // Revert the main_guest data to the original values and exit edit mode
      this.bookingData.main_guest = { ...this.originalGuestData };
      this.isEditing = false;
    },
    isBookingNotCancelled() {
      return this.bookingData.status !== 'CANCELLED';
    },
    validateEmail() {
      const email = this.bookingData.main_guest.email;
      this.isEmailValid = this.isValidEmailOrBlank(email);
    },
    isValidEmailOrBlank(email) {
      const normalized = (email || '').toString().trim();
      if (!normalized) return true;
      // Allow + character in email (valid for Gmail plus addressing and relay emails)
      const emailPattern = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailPattern.test(normalized);
    },
    showErrorMessage(message) {
      const errorMessageElem = document.getElementById('error-message');
      errorMessageElem.textContent = message;
      errorMessageElem.style.opacity = '0';
      errorMessageElem.style.display = 'block';
      setTimeout(() => {
        errorMessageElem.style.opacity = '1';
      }, 500);
    },

    hideErrorMessage() {
      const errorMessageElem = document.getElementById('error-message');
      errorMessageElem.style.opacity = '0';
      setTimeout(() => {
        errorMessageElem.style.display = 'none';
      }, 500);
    },
    formatDateForInput(date) {
      if (!date) return '';
      const [day, month, year] = date.split('/');
      return `${year}-${month}-${day}`;
    },

    formatDateForDisplay(date) {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },
    formatActivityDateTime(value) {
      if (!value) return '-';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      return parsed.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    getEmailDeliveryStatusClass(status) {
      const statusClasses = {
        sent: 'bg-success',
        failed: 'bg-danger',
        permanent_failed: 'bg-danger',
        queued: 'bg-warning text-dark',
        pending: 'bg-secondary',
        skipped: 'bg-info text-dark',
      };
      return statusClasses[status] || 'bg-secondary';
    },
    getWeekdaysDisplay(weekdays) {
      if (!weekdays || weekdays.length === 0) return '';

      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const selectedDays = weekdays
        .map((w) => Number(w))
        .sort((a, b) => a - b)
        .map((w) => dayNames[w]);

      // If all days selected, show "All days"
      if (selectedDays.length === 7) return 'All days';

      // If consecutive days, show range (e.g., "Mon-Thu")
      if (this.isConsecutiveDays(weekdays)) {
        return `${selectedDays[0]}-${selectedDays[selectedDays.length - 1]}`;
      }

      // Otherwise show comma-separated list
      return selectedDays.join(', ');
    },

    isConsecutiveDays(weekdays) {
      if (!weekdays || weekdays.length < 2) return false;

      const sorted = weekdays.map((w) => Number(w)).sort((a, b) => a - b);

      // Check if days are consecutive (accounting for week wrap-around)
      for (let i = 1; i < sorted.length; i++) {
        const diff = sorted[i] - sorted[i - 1];
        if (diff !== 1 && !(sorted[i] === 0 && sorted[i - 1] === 6)) {
          return false;
        }
      }
      return true;
    },

    formatCurrency(amount, currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
      }).format(parseFloat(amount));
    },
    formatFileSize(bytes) {
      const size = Number(bytes || 0);
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    },
    /** True when the person icon (fa-person) should be shown; false when a non-default person label override is in use (e.g. Cat/Cats). */
    showPersonIcon(rateSet) {
      if (!rateSet) return true;
      const raw = rateSet.person_label_singular;
      if (raw == null || raw === '') return true;
      const s = String(raw).trim();
      return !s || s.toLowerCase() === 'person';
    },
    getChangeLogEntries() {
      return Array.from(this.bookingData?.change_log || [])
        .filter((entry) => !!entry)
        .reverse();
    },
    getChangeIconClass(changeType) {
      const iconByType = {
        created: 'fa-solid fa-plus-circle text-dark fa-lg',
        source_partner_created: 'fa-solid fa-plus-circle text-dark fa-lg',
        source_admin_created: 'fa-solid fa-plus-circle text-dark fa-lg',
        source_ota_created: 'fa-solid fa-plus-circle text-dark fa-lg',
        source_ota_modified: 'fa-solid fa-arrows-rotate text-dark fa-lg',
        status_changed: 'fa-solid fa-flag text-dark fa-lg',
        cancelled: 'fa-solid fa-flag text-dark fa-lg',
        checkin_status_changed: 'fa-solid fa-door-open text-dark fa-lg',
        price_adjusted: 'fa-solid fa-euro-sign text-dark fa-lg',
        item_updated: 'fa-solid fa-euro-sign text-dark fa-lg',
        discount_applied: 'fa-solid fa-tag text-dark fa-lg',
        discount_removed: 'fa-solid fa-tag text-muted fa-lg',
        transaction_added: 'fa-solid fa-credit-card text-dark fa-lg',
        transaction_removed: 'fa-solid fa-credit-card text-dark fa-lg',
        customer_updated: 'fa-solid fa-user-pen text-dark fa-lg',
        guest_updated: 'fa-solid fa-user-pen text-dark fa-lg',
        guest_added: 'fa-solid fa-user-plus text-dark fa-lg',
        guest_removed: 'fa-solid fa-user-minus text-dark fa-lg',
        booking_confirmed: 'fa-solid fa-check-circle text-dark fa-lg',
        booking_submitted: 'fa-solid fa-check-circle text-dark fa-lg',
        extra_added: 'fa-solid fa-star text-dark fa-lg',
        extra_removed: 'fa-solid fa-star text-dark fa-lg',
        extra_updated: 'fa-solid fa-star text-dark fa-lg',
        item_removed: 'fa-solid fa-trash text-muted fa-lg',
        archived: 'fa-solid fa-box-archive text-muted fa-lg',
        unarchived: 'fa-solid fa-box-open text-muted fa-lg',
        partner_associated: 'fa-solid fa-handshake text-dark fa-lg',
        partner_removed: 'fa-solid fa-handshake-slash text-muted fa-lg',
        partner_code_updated: 'fa-solid fa-key text-dark fa-lg',
        partner_commission_updated: 'fa-solid fa-percent text-dark fa-lg',
        invoice_draft_created: 'fa-solid fa-file-invoice text-dark fa-lg',
        invoice_issued: 'fa-solid fa-file-invoice text-dark fa-lg',
        invoice_auto_issued: 'fa-solid fa-file-invoice text-dark fa-lg',
        invoice_reissued: 'fa-solid fa-file-invoice text-dark fa-lg',
        partner_invoice_created: 'fa-solid fa-file-invoice text-dark fa-lg',
        credit_note_created:
          'fa-solid fa-file-invoice-dollar text-warning fa-lg',
        invoice_refunded: 'fa-solid fa-file-invoice-dollar text-warning fa-lg',
      };
      return iconByType[changeType] || 'fa-solid fa-pen text-muted fa-lg';
    },
    getChangeTypeLabel(entry) {
      const type = entry?.change_type || entry?.legacy_change_type || 'updated';
      return type.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    },
    getChangeSourceLabel(source) {
      const sourceLabels = {
        full_booking: 'Full Booking',
        online_store: 'Online Store',
        online_store_admin: 'Online Store Admin',
        online_store_partner: 'Online Store Partner',
        partner_login: 'Partner Login',
        partner_logout: 'Partner Logout',
        payments: 'Payments',
        payment_request: 'Payment Request',
        store_checkout: 'Store Checkout',
        store_webhook: 'Store Webhook',
        store_extras: 'Store Extras',
        customer_update: 'Customer Update',
        customer_merge: 'Customer Merge',
        channex_import: 'Channex Import',
        channex_webhook: 'Channex Webhook',
        reports_task: 'Reports Task',
        invoicexpress_task: 'InvoiceXpress Task',
        invoicexpress_auto_task: 'InvoiceXpress Auto Task',
        invoicexpress_bulk_task: 'InvoiceXpress Bulk Task',
      };
      if (!source) return 'System';
      return sourceLabels[source] || source.replaceAll('_', ' ');
    },
    formatChangeValue(value) {
      if (value && typeof value === 'object') {
        if (value.display !== undefined && value.display !== null)
          return String(value.display);
        if (value.value !== undefined && value.value !== null)
          return String(value.value);
      }
      if (value === null || value === undefined || value === '')
        return '(empty)';
      return String(value);
    },
    formatAmount(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return String(value || '0.00');
      return number.toFixed(2);
    },
    async adjustBookingItemPrice(bookingItemId, newPrice) {
      const toastStore = Alpine.store('toastStore');
      try {
        const response = await fetch(
          `/bookings_app/adjust-booking-item-price/${bookingItemId}/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              new_price: newPrice,
            }),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to adjust price');
        }

        const data = await response.json();
        if (data.status === 'success') {
          // Refresh booking data to show updated prices
          await this.fetchBookingData(this.bookingData.booking_id);
          this.bookingData.status = data.booking_status;
          this.bookingData.human_readable_status = data.human_readable_status;
          toastStore.addToast('Price adjusted successfully', 'success');
        } else {
          throw new Error(data.message || 'Failed to adjust price');
        }
      } catch (error) {
        console.error('Error adjusting price:', error);
        toastStore.addToast(
          error.message || 'Failed to adjust price',
          'danger',
        );
      }
    },
    async deleteBookingExtra(extraId) {
      const toastStore = Alpine.store('toastStore');
      try {
        const response = await fetch(
          `/fullbooking/booking-extra-archive/${extraId}/`,
          {
            method: 'POST',
            headers: {
              'X-CSRFToken': getCSRFToken(),
              'Content-Type': 'application/json',
            },
          },
        );

        const data = await response.json();
        if (data.status === 'success') {
          console.log('Deleted booking extra successfully');
          await this.fetchBookingData(this.bookingData.booking_id);
          toastStore.addToast('Extra deleted successfully', 'success');
        } else {
          throw new Error(data.message || 'Failed to delete extra');
        }
      } catch (error) {
        console.error('Failed to delete booking extra:', error);
        toastStore.addToast(
          error.message || 'Failed to delete extra',
          'danger',
        );
      }
    },

    async adjustBookingExtraPrice(extraId, newPrice) {
      const toastStore = Alpine.store('toastStore');
      try {
        const response = await fetch(
          `/fullbooking/adjust-booking-extra-price/${extraId}/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              new_price: newPrice,
            }),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to adjust price');
        }

        const data = await response.json();
        if (data.status === 'success') {
          // Refresh booking data to show updated prices
          await this.fetchBookingData(this.bookingData.booking_id);
          toastStore.addToast('Price adjusted successfully', 'success');
        } else {
          throw new Error(data.message || 'Failed to adjust price');
        }
      } catch (error) {
        console.error('Error adjusting extra price:', error);
        toastStore.addToast(
          error.message || 'Failed to adjust price',
          'danger',
        );
      }
    },
    async fetchCalendarExtras() {
      try {
        const response = await fetch(
          `/fullbooking/calendar-extras/${this.bookingData.booking_id}/`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );

        if (!response.ok) {
          throw new Error('Failed to fetch extras');
        }

        const data = await response.json();
        this.availableExtras = data.extras;
        this.showExtrasModal = true;
      } catch (error) {
        console.error('Error fetching extras:', error);
        const toastStore = Alpine.store('toastStore');
        toastStore.addToast(
          error.message || 'Failed to fetch extras',
          'danger',
        );
      }
    },

    async addExtrasToBooking(extraId, amount, unitPrice) {
      const toastStore = Alpine.store('toastStore');
      try {
        const response = await fetch(
          `/fullbooking/add-booking-extras/${this.bookingData.booking_id}/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              extra_id: extraId,
              amount: parseInt(amount),
              unit_price: unitPrice,
            }),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to add extras');
        }

        const data = await response.json();
        if (data.status === 'success') {
          await this.fetchBookingData(this.bookingData.booking_id);
        } else {
          throw new Error(data.message || 'Failed to add extras');
        }
      } catch (error) {
        console.error('Error adding extras:', error);
        toastStore.addToast(error.message || 'Failed to add extras', 'danger');
      }
    },
    async handleExtraImageUpload(event, mode = 'new') {
      // First, check if Pica is available
      if (typeof Pica !== 'function') {
        console.error(
          'Pica library not found. Make sure it is properly imported.',
        );
        // Fallback to basic handling
        const file = event.target.files[0];
        if (!file) return;

        // Check file size
        const maxSizeInBytes = 500 * 1024; // 500 KB
        if (file.size > maxSizeInBytes) {
          alert('Image file size must be less than 500KB');
          event.target.value = ''; // Clear the input
          return;
        }

        // Store the file for form submission
        if (mode === 'edit') {
          this.editingExtra.image = file;
        } else {
          this.newExtra.image = file;
        }

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          if (mode === 'edit') {
            this.editingExtra.imagePreview = e.target.result;
          } else {
            this.newExtra.imagePreview = e.target.result;
          }
        };

        reader.readAsDataURL(file);
        return;
      }

      const pica = new Pica({
        features: ['js', 'wasm'], // Use JS and WASM but not web workers due to CSP restrictions
      });
      const file = event.target.files[0];
      if (!file) return;

      const maxSizeInBytes = 500 * 1024; // 500 KB
      const maxWidth = 1920;
      const maxHeight = 1080;

      this.imageProcessing = true;

      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const img = new Image();
          img.src = e.target.result;

          img.onload = async () => {
            // Create initial canvas for aspect ratio correction
            let canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d', { willReadFrequently: true });

            const aspectRatio = img.width / img.height;
            const desiredAspectRatio = 16 / 9;

            if (aspectRatio < desiredAspectRatio) {
              // Portrait mode: crop to landscape
              const cropHeight = img.width / desiredAspectRatio;
              const totalCrop = img.height - cropHeight;
              const topCrop = totalCrop * 0.6;
              canvas.width = img.width;
              canvas.height = cropHeight;
              ctx.drawImage(
                img,
                0,
                topCrop,
                img.width,
                cropHeight,
                0,
                0,
                canvas.width,
                canvas.height,
              );
            } else {
              // Landscape mode: crop from top
              const cropTop = img.height * 0.2;
              const croppedHeight = img.height * 0.8;
              const cropWidth = croppedHeight * desiredAspectRatio;

              canvas.width = cropWidth;
              canvas.height = croppedHeight;
              ctx.drawImage(
                img,
                (img.width - cropWidth) / 2,
                cropTop,
                cropWidth,
                croppedHeight,
                0,
                0,
                canvas.width,
                canvas.height,
              );
            }

            // Resize if needed using Pica
            if (canvas.width > maxWidth || canvas.height > maxHeight) {
              const resizedCanvas = document.createElement('canvas');
              const width = canvas.width;
              const height = canvas.height;
              const scaleFactor = Math.min(
                maxWidth / width,
                maxHeight / height,
              );
              resizedCanvas.width = width * scaleFactor;
              resizedCanvas.height = height * scaleFactor;

              await pica.resize(canvas, resizedCanvas);
              canvas = resizedCanvas;
            }

            // Convert canvas to data URL
            const dataUrl = canvas.toDataURL(file.type, 0.85); // Added quality parameter

            // Convert data URL to Blob to check size
            const binaryString = atob(dataUrl.split(',')[1]);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: file.type });

            // Check if the processed image is still too large
            if (blob.size > maxSizeInBytes) {
              alert(
                'Processed image is still too large. Please use a smaller image.',
              );
              this.imageProcessing = false;
              return;
            }

            // Store both the processed blob and the data URL preview
            if (mode === 'edit') {
              this.editingExtra.image = new File([blob], file.name, {
                type: file.type,
              });
              this.editingExtra.imagePreview = dataUrl;
            } else {
              this.newExtra.image = new File([blob], file.name, {
                type: file.type,
              });
              this.newExtra.imagePreview = dataUrl;
            }
            this.imageProcessing = false;
          };
        };

        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error handling image upload:', error);
        this.imageProcessing = false;
        alert(
          'Error processing image. Please try again with a different image.',
        );
      }
    },

    removeExtraImage() {
      this.newExtra.image = null;
    },

    resetExtraForm() {
      this.newExtra = {
        name: '',
        description: '',
        unit_price: 0,
        image: null,
        imagePreview: null,
        tax_fees: [], // Initialize tax_fees as an empty array
        published: false,
        dropdown_field_title: '',
        dropdown_options_text: '',
      };
      this.imageProcessing = false;
    },

    async createExtra() {
      const toastStore = Alpine.store('toastStore');
      try {
        const formData = new FormData();
        formData.append('name', this.newExtra.name);
        formData.append('description', this.newExtra.description || '');
        formData.append('unit_price', this.newExtra.unit_price);
        formData.append('published', this.newExtra.published);
        if (this.newExtra.image) {
          formData.append('image', this.newExtra.image);
        }
        // Add tax_fees as a JSON string
        if (this.newExtra.tax_fees && this.newExtra.tax_fees.length > 0) {
          // Extract just the IDs from the tax_fees array
          const taxFeeIds = this.newExtra.tax_fees.map((tf) => tf.id);
          formData.append('tax_fees', JSON.stringify(taxFeeIds));
        }

        // Add dropdown field configuration
        if (this.newExtra.dropdown_field_title) {
          formData.append(
            'dropdown_field_title',
            this.newExtra.dropdown_field_title,
          );
        }
        if (this.newExtra.dropdown_options_text) {
          // Convert comma-separated string to JSON array
          const options = this.newExtra.dropdown_options_text
            .split(',')
            .map((option) => option.trim())
            .filter((option) => option.length > 0);
          if (options.length > 0) {
            formData.append('dropdown_options', JSON.stringify(options));
          }
        }

        const response = await fetch(
          `/fullbooking/create-extra/${this.bookingData.booking_id}/`,
          {
            method: 'POST',
            headers: {
              'X-CSRFToken': getCSRFToken(),
            },
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error('Failed to create extra');
        }

        const data = await response.json();
        if (data.status === 'success') {
          // Refresh the available extras list
          await this.fetchCalendarExtras();
          toastStore.addToast('Extra created successfully', 'success');
          this.resetExtraForm();
          this.showCreateExtraModal = false;
        } else {
          throw new Error(data.message || 'Failed to create extra');
        }
      } catch (error) {
        console.error('Error creating extra:', error);
        toastStore.addToast(
          error.message || 'Failed to create extra',
          'danger',
        );
      }
    },
    async submitNewTaxFee() {
      try {
        // Get calendar_id from the booking data
        const calendar_id = this.bookingData.calendar;

        const response = await fetch('/rates/create-tax-fee/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            calendar_id: calendar_id,
            is_tax: this.newTaxFee.is_tax,
            name: this.newTaxFee.name,
            amount: parseFloat(this.newTaxFee.amount),
            type: this.newTaxFee.type,
            is_inclusive:
              this.newTaxFee.type === 'PERCENTAGE'
                ? this.newTaxFee.is_inclusive
                : false,
            applies_to_fees: this.newTaxFee.is_tax
              ? this.newTaxFee.applies_to_fees
              : false,
          }),
        });

        const data = await response.json();

        if (data.success) {
          // Add to appropriate list
          const newTaxFee = data.tax_fee;
          if (newTaxFee.is_tax) {
            this.availableTaxes.push(newTaxFee);
          } else {
            this.availableFees.push(newTaxFee);
          }

          // Add to current extra
          this.newExtra.tax_fees.push(newTaxFee);

          this.closeNewTaxFeeModal();
          const toastStore = Alpine.store('toastStore');
          toastStore.addToast('Tax/Fee created successfully', 'success');
        } else {
          throw new Error(data.error || 'Failed to create tax/fee');
        }
      } catch (error) {
        console.error('Error creating tax/fee:', error);
        const toastStore = Alpine.store('toastStore');
        toastStore.addToast(
          error.message || 'Failed to create tax/fee',
          'danger',
        );
      }
    },
    resetNewTaxFeeForm() {
      this.newTaxFee = {
        is_tax: true,
        name: '',
        amount: 0,
        type: 'PERCENTAGE',
        is_inclusive: false,
        applies_to_fees: false,
      };
    },

    // Update the fetchTaxesAndFees method to initialize arrays if the fetch fails
    async fetchTaxesAndFees() {
      try {
        const response = await fetch(
          `/fullbooking/available-tax-fees/?booking_id=${this.bookingData.booking_id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );

        if (!response.ok) throw new Error('Failed to fetch taxes and fees');

        const data = await response.json();
        this.availableTaxes = data.taxes || [];
        this.availableFees = data.fees || [];
      } catch (error) {
        console.error('Error fetching taxes and fees:', error);
        this.availableTaxes = [];
        this.availableFees = [];
      }
    },

    // Update the template filters to handle undefined/null cases
    getAvailableTaxes(mode = 'new') {
      const currentTaxFees =
        mode === 'new' ? this.newExtra.tax_fees : this.editingExtra.tax_fees;
      return (this.availableTaxes || []).filter(
        (t) => !(currentTaxFees || []).some((tf) => tf.id === t.id),
      );
    },

    getAvailableFees(mode = 'new') {
      const currentTaxFees =
        mode === 'new' ? this.newExtra.tax_fees : this.editingExtra.tax_fees;
      return (this.availableFees || []).filter(
        (f) => !(currentTaxFees || []).some((tf) => tf.id === f.id),
      );
    },

    // Modal methods
    openNewTaxFeeModal(source = 'new') {
      this.taxFeeModalSource = source; // Store where we came from
      this.showNewTaxFeeModal = true;
      this.showCreateExtraModal = false;
      this.showEditExtraModal = false;
    },

    closeNewTaxFeeModal() {
      this.showNewTaxFeeModal = false;
      if (this.taxFeeModalSource === 'edit') {
        this.showEditExtraModal = true;
      } else {
        this.showCreateExtraModal = true;
      }
      this.resetNewTaxFeeForm();
      this.taxFeeModalSource = null;
    },

    resetNewTaxFeeForm() {
      this.newTaxFee = {
        is_tax: true,
        name: '',
        amount: 0,
        type: 'PERCENTAGE',
        is_inclusive: false,
        applies_to_fees: false,
      };
    },

    async openCreateExtraModal() {
      // Reset the form first
      this.newExtra = {
        name: '',
        description: '',
        unit_price: 0,
        image: null,
        imagePreview: null,
        tax_fees: [], // Make sure this is initialized as an empty array
        published: false,
      };

      await this.fetchTaxesAndFees();
      this.showCreateExtraModal = true;
    },

    addTaxFee(taxFeeId, mode = 'new') {
      if (!taxFeeId) return;

      const numericId = Number(taxFeeId);
      const tax = this.availableTaxes.find((t) => t.id === numericId);
      const fee = this.availableFees.find((f) => f.id === numericId);
      const taxFee = tax || fee;

      if (taxFee) {
        const target = mode === 'new' ? this.newExtra : this.editingExtra;
        if (!target.tax_fees) {
          target.tax_fees = [];
        }
        if (!target.tax_fees.some((tf) => tf.id === taxFee.id)) {
          target.tax_fees.push(taxFee);
        }
      }
    },

    removeTaxFee(taxFeeId, mode = 'new') {
      const target = mode === 'new' ? this.newExtra : this.editingExtra;
      target.tax_fees = target.tax_fees.filter((tf) => tf.id !== taxFeeId);
    },

    async openEditExtraModal(extra) {
      console.log('Opening edit modal with extra:', extra);

      try {
        // First fetch the latest tax_fees data
        await this.fetchTaxesAndFees();

        // Fetch the full extra details including tax_fees
        const response = await fetch(
          `/fullbooking/calendar-extras/${this.bookingData.booking_id}/`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );

        if (!response.ok) throw new Error('Failed to fetch extra details');

        const data = await response.json();
        const fullExtra = data.extras.find((e) => e.id === extra.id);

        if (!fullExtra) throw new Error('Extra not found');

        console.log('Full extra details:', fullExtra);

        // Initialize editingExtra with all the extra's data
        this.editingExtra = {
          id: fullExtra.id,
          name: fullExtra.name,
          description: fullExtra.description || '',
          unit_price: fullExtra.unit_price,
          image: null,
          imagePreview: fullExtra.image_url,
          tax_fees: fullExtra.tax_fees || [],
          published: fullExtra.published,
        };

        console.log('Initialized editingExtra:', this.editingExtra);

        // Show edit modal and hide extras modal
        this.showEditExtraModal = true;
        this.showExtrasModal = false;
      } catch (error) {
        console.error('Error opening edit modal:', error);
        const toastStore = Alpine.store('toastStore');
        toastStore.addToast('Failed to load extra details', 'danger');
      }
    },

    closeEditExtraModal() {
      this.showEditExtraModal = false;
      this.showExtrasModal = true;
      this.editingExtra = {
        id: null,
        name: '',
        description: '',
        unit_price: 0,
        image: null,
        imagePreview: null,
        tax_fees: [],
        published: false,
      };
    },

    async updateExtra() {
      try {
        const formData = new FormData();
        formData.append('name', this.editingExtra.name);
        formData.append('description', this.editingExtra.description);
        formData.append('unit_price', this.editingExtra.unit_price);
        formData.append('published', this.editingExtra.published);

        if (this.editingExtra.image) {
          formData.append('image', this.editingExtra.image);
        }

        if (
          this.editingExtra.tax_fees &&
          this.editingExtra.tax_fees.length > 0
        ) {
          const taxFeeIds = this.editingExtra.tax_fees.map((tf) => tf.id);
          formData.append('tax_fees', JSON.stringify(taxFeeIds));
        }

        const response = await fetch(
          `/fullbooking/update-extra/${this.editingExtra.id}/`,
          {
            method: 'PUT',
            headers: {
              'X-CSRFToken': getCSRFToken(),
            },
            body: formData,
          },
        );

        if (!response.ok) throw new Error('Failed to update extra');

        // Refresh the extras list
        await this.fetchCalendarExtras();

        // Close modal and show success message
        this.closeEditExtraModal();
        const toastStore = Alpine.store('toastStore');
        toastStore.addToast('Extra updated successfully', 'success');
      } catch (error) {
        console.error('Error updating extra:', error);
        const toastStore = Alpine.store('toastStore');
        toastStore.addToast(
          error.message || 'Failed to update extra',
          'danger',
        );
      }
    },

    hasSelectedExtras() {
      return this.availableExtras.some((extra) => extra.amount > 0);
    },

    async addSelectedExtras() {
      const selectedExtras = this.availableExtras.filter(
        (extra) => extra.amount > 0,
      );
      const toastStore = Alpine.store('toastStore');

      try {
        // Process each selected extra
        for (const extra of selectedExtras) {
          await this.addExtrasToBooking(
            extra.id,
            extra.amount,
            extra.unit_price,
          );
        }

        // Close modal after all extras are added
        this.showExtrasModal = false;

        // Reset amounts
        this.availableExtras.forEach((extra) => (extra.amount = 0));

        toastStore.addToast('Extras added successfully', 'success');
      } catch (error) {
        console.error('Error adding extras:', error);
        toastStore.addToast(error.message || 'Failed to add extras', 'danger');
      }
    },

    async deleteExtra(extraId) {
      const toastStore = Alpine.store('toastStore');
      try {
        const response = await fetch(`/fullbooking/delete-extra/${extraId}/`, {
          method: 'DELETE',
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
        });

        if (!response.ok) throw new Error('Failed to delete extra');

        const data = await response.json();
        if (data.status === 'success') {
          // Refresh the extras list
          await this.fetchCalendarExtras();
          toastStore.addToast('Extra deleted successfully', 'success');
        } else {
          throw new Error(data.message || 'Failed to delete extra');
        }
      } catch (error) {
        console.error('Error deleting extra:', error);
        toastStore.addToast(
          error.message || 'Failed to delete extra',
          'danger',
        );
      }
    },

    initializePaymentRequest() {
      console.log('Initializing payment request'); // Debug log
      this.paymentRequest = {
        amount: this.bookingData.amount_due,
        dueDate: '',
        note: '',
        cancelIfPendingOnExpiry: false,
        sendReminder: false,
        reminderDaysBeforeDue: 1,
        isSubmitting: false,
      };
    },

    formatDateTimeForInput(dateTimeValue) {
      if (!dateTimeValue) return '';
      const date = new Date(dateTimeValue);
      if (Number.isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },

    syncPendingExpirationDraft() {
      this.pendingExpirationDraft = this.formatDateTimeForInput(
        this.bookingData.pending_expires_at,
      );
    },

    async updatePendingExpiration() {
      if (this.updatingPendingExpiration) return;
      this.updatingPendingExpiration = true;
      try {
        const response = await fetch(
          `/fullbooking/update-pending-expiration/${this.bookingData.booking_id}/`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              pending_expires_at: this.pendingExpirationDraft || null,
            }),
          },
        );

        const data = await response.json();
        if (!response.ok || data.error) {
          throw new Error(data.error || 'Failed to update pending expiration');
        }

        if (data.booking) {
          this.bookingData = {
            ...this.bookingData,
            ...data.booking,
            booking_id: data.booking.booking_id || this.bookingData.booking_id,
          };
        }
        this.syncPendingExpirationDraft();
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast('Pending expiration updated', 'success');
        }
      } catch (error) {
        console.error('Error updating pending expiration:', error);
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            error.message || 'Failed to update pending expiration',
            'danger',
          );
        }
      } finally {
        this.updatingPendingExpiration = false;
      }
    },

    clearPendingExpiration() {
      this.pendingExpirationDraft = '';
      this.updatePendingExpiration();
    },

    isBookingPendingStatus() {
      const status = String(this.bookingData?.status || '')
        .trim()
        .toUpperCase();
      const humanReadableStatus = String(
        this.bookingData?.human_readable_status || '',
      )
        .trim()
        .toUpperCase();
      return status === 'PENDING' || humanReadableStatus === 'PENDING';
    },

    togglePaymentRequestModal() {
      this.showPaymentRequestModal = !this.showPaymentRequestModal;
      // Always initialize to ensure the amount due on amount field is correct
      this.initializePaymentRequest();
    },

    validatePaymentAmount(value) {
      const amount = parseFloat(value);
      if (amount > this.bookingData.amount_due) {
        this.paymentRequest.amount = this.bookingData.amount_due;
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            'Amount cannot exceed the amount due',
            'warning',
          );
        }
      }
    },

    hasMainGuestEmail() {
      const email = String(this.bookingData?.main_guest?.email || '').trim();
      return Boolean(email);
    },

    getPaymentRequestDisabledReason() {
      if (this.bookingData.amount_due <= 0) {
        return 'No amount due';
      }
      if (!this.hasMainGuestEmail()) {
        return 'Main guest email is required to send payment requests';
      }
      if (
        !this.bookingData.calendar.stripe_connect_enabled ||
        !this.bookingData.calendar.stripe_connect_onboarding_completed
      ) {
        return 'Connect Stripe to enable payment requests';
      }
      return 'Send Payment Request using Stripe';
    },

    async submitPaymentRequest() {
      if (!this.hasMainGuestEmail()) {
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            'Main guest email is required before sending a payment request',
            'danger',
          );
        }
        return;
      }

      if (!this.paymentRequest.amount || this.paymentRequest.amount <= 0) {
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast('Please enter a valid amount', 'danger');
        }
        return;
      }

      if (this.paymentRequest.amount > this.bookingData.amount_due) {
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            'Amount cannot exceed the amount due',
            'danger',
          );
        }
        return;
      }

      // Check if this would exceed amount due with existing requests
      const totalPendingAmount =
        this.bookingData.pending_payment_requests_total || 0;
      if (
        totalPendingAmount + this.paymentRequest.amount >
        this.bookingData.amount_due
      ) {
        if (
          !confirm(
            'This will exceed the Amount due with existing requests. Older payment requests will be cancelled. Continue?',
          )
        ) {
          return;
        }
      }

      this.paymentRequest.isSubmitting = true;

      try {
        const formData = new FormData();
        formData.append('booking_id', this.bookingData.booking_id);
        formData.append('amount', this.paymentRequest.amount);
        if (this.paymentRequest.dueDate)
          formData.append('due_date', this.paymentRequest.dueDate);
        if (this.paymentRequest.note)
          formData.append('note', this.paymentRequest.note);
        const canCancelOnExpiry =
          this.isBookingPendingStatus() &&
          Boolean(this.paymentRequest.cancelIfPendingOnExpiry);
        formData.append(
          'cancel_if_pending_on_expiry',
          canCancelOnExpiry ? 'true' : 'false',
        );
        if (this.paymentRequest.sendReminder && this.paymentRequest.dueDate) {
          formData.append(
            'reminder_days_before_due',
            this.paymentRequest.reminderDaysBeforeDue || 1,
          );
        }

        const response = await fetch('/payments/create-payment-request/', {
          method: 'POST',
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
          body: formData,
        });

        const data = await response.json();

        if (response.ok) {
          // Update the pending total from the response
          this.bookingData.pending_payment_requests_total =
            data.pending_payment_requests_total;

          // Show success message
          if (this.toastStore && this.toastStore.addToast) {
            this.toastStore.addToast(
              'Payment request sent successfully',
              'success',
            );
          }

          // If there's a warning about cancelled requests, show it
          if (data.warning) {
            if (this.toastStore && this.toastStore.addToast) {
              this.toastStore.addToast(data.warning, 'warning');
            }
          }

          // Refresh booking data so Pending Expiration UI stays in sync.
          await this.fetchBookingData(this.bookingData.booking_id);

          this.showPaymentRequestModal = false;
          setTimeout(() => {
            this.initializePaymentRequest();
          }, 300); // 300ms delay to match modal transition
        } else {
          throw new Error(data.error || 'Failed to create payment request');
        }
      } catch (error) {
        console.error('Error creating payment request:', error);
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            error.message || 'Failed to create payment request',
            'danger',
          );
        }
      } finally {
        this.paymentRequest.isSubmitting = false;
      }
    },

    getHumanReadableCheckinStatus() {
      // Use the human_readable_checkin_status from the backend if available
      if (this.bookingData.human_readable_checkin_status) {
        return this.bookingData.human_readable_checkin_status;
      }

      // Fallback to hardcoded mapping if backend doesn't provide it
      const statusMap = {
        '': 'Not Set',
        CHECKED_IN: 'Checked In',
        CHECKED_OUT: 'Checked Out',
        NO_SHOW: 'No Show',
      };
      return statusMap[this.bookingData.checkin_status || ''] || 'Not Set';
    },

    getCheckinStatusLabel(statusKey) {
      // Use custom labels from the backend if available
      if (
        this.bookingData.checkin_status_labels &&
        this.bookingData.checkin_status_labels[statusKey]
      ) {
        return this.bookingData.checkin_status_labels[statusKey];
      }

      // Fallback to hardcoded mapping
      const statusMap = {
        '': 'Not Set',
        CHECKED_IN: 'Checked In',
        CHECKED_OUT: 'Checked Out',
        NO_SHOW: 'No Show',
      };
      return statusMap[statusKey] || 'Not Set';
    },

    updateCheckinStatus(status) {
      const bookingId = this.bookingData.booking_id;

      fetch(`/fullbooking/update-checkin-status/${bookingId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.getCsrfToken(),
        },
        body: JSON.stringify({
          checkin_status: status,
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((data) => {
          this.bookingData.checkin_status = status;
          // Show success notification if needed
        })
        .catch((error) => {
          console.error('Error updating check-in status:', error);
          // Show error notification if needed
        });
    },

    getCsrfToken() {
      return document.querySelector('[name=csrfmiddlewaretoken]').value;
    },

    // Invoice functionality
    initializeInvoiceData() {
      // Initialize customer business data
      // Auto-determine invoice recipient from partner rules
      const partner = this.bookingData?.partner;
      const isPartnerInvoicing =
        partner && partner.invoicing_mode === 'PARTNER';

      // Store partner data
      const partnerData = {
        business_name: partner?.business_name || partner?.name || '',
        vat_number: partner?.vat_number || '',
      };

      if (isPartnerInvoicing) {
        // Partner invoicing mode - invoice goes to partner
        this.invoiceData = {
          customer_business_name: partnerData.business_name,
          customer_vat_number: partnerData.vat_number,
          invoice_notes: '',
          isGenerating: false,
          is_partner_invoice: true,
          partner_name: partner?.name || '',
        };
      } else {
        // Standard customer invoice (starts empty)
        this.invoiceData = {
          customer_business_name: '',
          customer_vat_number: '',
          invoice_notes: '',
          isGenerating: false,
          is_partner_invoice: false,
          partner_name: '',
        };
      }
    },

    async toggleInvoiceModal() {
      this.showInvoiceModal = !this.showInvoiceModal;
      if (this.showInvoiceModal) {
        this.initializeInvoiceData();
        // Check InvoiceXpress and load documents when modal opens
        if (!this.invoiceXpress.hasCheckedEnabled) {
          await this.checkInvoiceXpressEnabled();
          if (this.invoiceXpress.enabled) {
            await this.loadInvoiceXpressDocuments();
            // Load countries for InvoiceXpress client form dropdown
            if (this.countries.length === 0) {
              await this.fetchCountries();
            }
            // Initialize client data from main guest
            this.initInvoiceXpressClientData();
          }
          this.invoiceXpress.hasCheckedEnabled = true;
        } else if (this.invoiceXpress.enabled) {
          // If already checked, still load countries if needed and init client data
          if (this.countries.length === 0) {
            await this.fetchCountries();
          }
          this.initInvoiceXpressClientData();
        }
      } else {
        // Reset state when modal closes
        this.resetInvoiceXpressState();
      }
    },

    async generateInvoice() {
      // No validation needed - all fields are optional
      // Invoice will use calendar name as fallback for business name
      this.invoiceData.isGenerating = true;

      try {
        const formData = new FormData();
        formData.append(
          'customer_business_name',
          this.invoiceData.customer_business_name.trim(),
        );
        formData.append(
          'customer_vat_number',
          this.invoiceData.customer_vat_number.trim(),
        );
        formData.append('invoice_notes', this.invoiceData.invoice_notes.trim());
        // Partner invoicing flag
        formData.append(
          'is_partner_invoice',
          this.invoiceData.is_partner_invoice ? 'true' : 'false',
        );

        const response = await fetch(
          `/fullbooking/generate-invoice/${this.bookingData.booking_id}/`,
          {
            method: 'POST',
            body: formData,
            headers: {
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );

        const data = await response.json();

        if (response.ok && data.success && data.task_id) {
          // Task queued successfully, start polling
          if (this.toastStore && this.toastStore.addToast) {
            this.toastStore.addToast(
              'Invoice generation is processing in the background. This may take a few moments.',
              'info',
            );
          }
          // Start polling for task status
          this.pollInvoiceStatus(data.task_id);
        } else {
          // Error queueing task
          if (this.toastStore && this.toastStore.addToast) {
            this.toastStore.addToast(
              data.message || 'Error queueing invoice generation',
              'danger',
            );
          }
          this.invoiceData.isGenerating = false;
        }
      } catch (error) {
        console.error('Error queueing invoice generation:', error);
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            'Error queueing invoice generation',
            'danger',
          );
        }
        this.invoiceData.isGenerating = false;
      }
    },

    async pollInvoiceStatus(taskId) {
      const maxAttempts = 120; // Poll for up to 10 minutes (5 sec intervals)
      let attempts = 0;

      const checkStatus = async () => {
        try {
          const response = await fetch(
            `/fullbooking/invoice-status/${taskId}/`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            },
          );

          const data = await response.json();
          console.log('Invoice task status:', data);

          if (data.status === 'completed') {
            if (data.success) {
              // Download the PDF
              const downloadResponse = await fetch(
                `/fullbooking/invoice-download/${taskId}/`,
                {
                  method: 'GET',
                },
              );

              if (downloadResponse.ok) {
                // Get the filename from the response headers
                const contentDisposition = downloadResponse.headers.get(
                  'Content-Disposition',
                );
                let filename = data.filename || 'invoice.pdf';
                if (contentDisposition) {
                  const filenameMatch =
                    contentDisposition.match(/filename="(.+)"/);
                  if (filenameMatch) {
                    filename = filenameMatch[1];
                  }
                }

                // Create a blob and download the file
                const blob = await downloadResponse.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                // Close the modal and show success message
                this.showInvoiceModal = false;
                if (this.toastStore && this.toastStore.addToast) {
                  this.toastStore.addToast(
                    'Invoice generated successfully',
                    'success',
                  );
                }

                // Update calendar business data if this was the first time
                this.updateCalendarBusinessData();
              } else {
                if (this.toastStore && this.toastStore.addToast) {
                  this.toastStore.addToast(
                    'Error downloading invoice',
                    'danger',
                  );
                }
              }
            } else {
              if (this.toastStore && this.toastStore.addToast) {
                this.toastStore.addToast(
                  `Invoice generation failed: ${data.message}`,
                  'danger',
                );
              }
            }
            this.invoiceData.isGenerating = false;
            return; // Stop polling
          } else if (data.status === 'failed') {
            if (this.toastStore && this.toastStore.addToast) {
              this.toastStore.addToast(
                `Invoice generation failed: ${data.message}`,
                'error',
              );
            }
            this.invoiceData.isGenerating = false;
            return; // Stop polling
          } else if (
            data.status === 'pending' ||
            data.status === 'in_progress'
          ) {
            // Task is still processing, continue polling
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(checkStatus, 5000); // Poll every 5 seconds
            } else {
              if (this.toastStore && this.toastStore.addToast) {
                this.toastStore.addToast(
                  'Invoice generation is taking longer than expected. Please try again later.',
                  'warning',
                );
              }
              this.invoiceData.isGenerating = false;
            }
          }
        } catch (error) {
          console.error('Error checking invoice task status:', error);
          // Continue polling despite errors, but limit attempts
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 5000);
          } else {
            this.invoiceData.isGenerating = false;
          }
        }
      };

      // Start polling
      checkStatus();
    },

    async updateCalendarBusinessData() {
      // No longer needed since we're not collecting business data in the modal
      // Customer business data is per-invoice, not stored persistently
    },

    // InvoiceXpress functionality
    async checkInvoiceXpressEnabled() {
      try {
        const bookingId = this.bookingData?.booking_id;
        if (!bookingId) {
          console.warn('No booking ID available for InvoiceXpress check');
          return;
        }
        const response = await fetch(`/invoicexpress/enabled/${bookingId}/`);
        if (response.ok) {
          const data = await response.json();
          this.invoiceXpress.enabled = data.is_enabled;
        }
      } catch (error) {
        console.error('Error checking InvoiceXpress status:', error);
      }
    },

    async loadInvoiceXpressDocuments() {
      this.invoiceXpress.isLoadingDocuments = true;
      try {
        const bookingId = this.bookingData.booking_id;
        const response = await fetch(`/invoicexpress/documents/${bookingId}/`);
        if (response.ok) {
          const data = await response.json();
          this.invoiceXpress.documents = data.documents || [];
        }
      } catch (error) {
        console.error('Error loading documents:', error);
      } finally {
        this.invoiceXpress.isLoadingDocuments = false;
      }
    },

    initInvoiceXpressClientData() {
      // Check if booking has partner with partner invoicing mode
      const partner = this.bookingData?.partner;
      const isPartnerInvoicing =
        partner && partner.invoicing_mode === 'PARTNER';
      // Removed invoice_remainder_to_customer - booking is invoiced to partner OR customer, never both

      // Set partner invoicing flags
      // Auto-determine invoice recipient from partner rules (no toggle needed)
      this.invoiceXpress.partnerName = partner?.name || '';

      // Store customer client data from main guest
      const guest = this.bookingData.main_guest;
      const customerData = {
        name: guest
          ? `${guest.first_name || ''} ${guest.last_name || ''}`.trim()
          : '',
        email: guest?.email || '',
        address: guest?.address || '',
        country: '',
        fiscalId: '',
      };

      if (guest) {
        // Convert country_name to country code
        if (guest.country_name && this.countries.length > 0) {
          const country = this.countries.find(
            (c) => c.name === guest.country_name,
          );
          customerData.country = country ? country.code : '';
        } else if (guest.country) {
          customerData.country = guest.country;
        }

        // Check custom fields for VAT/NIF
        if (guest.custom_fields) {
          customerData.fiscalId =
            guest.custom_fields.vat_number ||
            guest.custom_fields.nif ||
            guest.custom_fields.fiscal_id ||
            guest.custom_fields.tax_id ||
            '';
        }
      }
      this.invoiceXpress._customerClientData = customerData;

      // Store partner client data if available
      if (isPartnerInvoicing && partner) {
        // billing_country is now a country code (e.g., "PT", "GB") from the API
        const partnerCountryCode = partner.billing_country || '';

        // Build partner address from billing fields
        const addressParts = [
          partner.billing_address,
          partner.billing_city,
          partner.billing_postal_code,
        ].filter(Boolean);

        this.invoiceXpress._partnerClientData = {
          name: partner.business_name || partner.name || '',
          email: partner.billing_email || '',
          address: addressParts.join(', '),
          country: partnerCountryCode,
          fiscalId: partner.vat_number || '',
        };
      } else {
        this.invoiceXpress._partnerClientData = null;
      }

      // Pre-fill with appropriate data based on invoicing mode
      if (isPartnerInvoicing && this.invoiceXpress._partnerClientData) {
        // Partner invoicing mode - default to partner invoice
        this.invoiceXpress.isPartnerInvoice = true;
        this.applyInvoiceXpressClientData(
          this.invoiceXpress._partnerClientData,
        );
        // Re-apply country after DOM updates so the country select shows the selected value
        const countryCode = this.invoiceXpress._partnerClientData.country;
        if (countryCode) {
          const alpine = typeof Alpine !== 'undefined' ? Alpine : window.Alpine;
          if (alpine && alpine.nextTick) {
            alpine.nextTick(() => {
              this.invoiceXpress.clientCountry = countryCode;
            });
          } else {
            setTimeout(() => {
              this.invoiceXpress.clientCountry = countryCode;
            }, 0);
          }
        }
      } else {
        // Customer invoicing mode
        this.invoiceXpress.isPartnerInvoice = false;
        this.applyInvoiceXpressClientData(customerData);
      }
    },

    // Apply client data to InvoiceXpress form fields
    applyInvoiceXpressClientData(data) {
      this.invoiceXpress.clientName = data.name || '';
      this.invoiceXpress.clientEmail = data.email || '';
      this.invoiceXpress.clientAddress = data.address || '';
      this.invoiceXpress.clientCountry = data.country || '';
      this.invoiceXpress.clientFiscalId = data.fiscalId || '';
    },

    // Helper to convert country code to country name for InvoiceXpress API
    getCountryNameFromCode(code) {
      if (!code) return undefined;
      const country = this.countries.find((c) => c.code === code);
      return country ? country.name : undefined;
    },

    async createDraftInvoice() {
      this.invoiceXpress.isCreatingDraft = true;
      try {
        const bookingId = this.bookingData.booking_id;

        // Build client data from form fields.
        // InvoiceXpress API expects ISO 3166 alpha-2 country code (e.g. GB, PT), not country name.
        const clientData = {
          name: this.invoiceXpress.clientName || undefined,
          email: this.invoiceXpress.clientEmail || undefined,
          fiscal_id: this.invoiceXpress.clientFiscalId || undefined,
          address: this.invoiceXpress.clientAddress || undefined,
          country: this.invoiceXpress.clientCountry || undefined,
        };

        // Remove undefined values
        Object.keys(clientData).forEach((key) => {
          if (clientData[key] === undefined) {
            delete clientData[key];
          }
        });

        const requestBody = {
          client: Object.keys(clientData).length > 0 ? clientData : undefined,
          is_partner_invoice: this.invoiceXpress.isPartnerInvoice,
        };

        const response = await fetch(`/invoicexpress/draft/${bookingId}/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(requestBody),
        });
        const data = await response.json();
        if (data.success) {
          this.toastStore.addToast(
            'Draft invoice created in InvoiceXpress',
            'success',
          );
          await this.pollInvoiceXpressTaskStatus(data.task_id);
          await this.loadInvoiceXpressDocuments();
          // Refresh staleness check
          this.checkInvoiceStaleness();
          // Reset the flag for creating remaining invoice
          this.invoiceXpress.showCreateFormForRemainingInvoice = false;
        } else {
          this.toastStore.addToast(
            data.error || 'Error creating draft',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error creating draft:', error);
        // Show the actual error message from InvoiceXpress if available
        const errorMessage = error.message || 'Error creating draft invoice';
        this.toastStore.addToast(errorMessage, 'danger');
      } finally {
        this.invoiceXpress.isCreatingDraft = false;
      }
    },

    async updateDraftInvoice(documentId, includeClientData = false) {
      try {
        // Build request body
        const body = {};
        if (includeClientData) {
          // InvoiceXpress API expects ISO 3166 alpha-2 country code (e.g. GB, PT)
          const clientData = {
            name: this.invoiceXpress.clientName || undefined,
            email: this.invoiceXpress.clientEmail || undefined,
            fiscal_id: this.invoiceXpress.clientFiscalId || undefined,
            address: this.invoiceXpress.clientAddress || undefined,
            country: this.invoiceXpress.clientCountry || undefined,
          };
          // Remove undefined values
          Object.keys(clientData).forEach((key) => {
            if (clientData[key] === undefined) {
              delete clientData[key];
            }
          });
          if (Object.keys(clientData).length > 0) {
            body.client = clientData;
          }
        }
        console.log('Request body:', body);

        const response = await fetch(
          `/invoicexpress/draft/${documentId}/update/`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify(body),
          },
        );
        const data = await response.json();
        if (data.success) {
          this.toastStore.addToast('Draft invoice updated', 'success');
          await this.pollInvoiceXpressTaskStatus(data.task_id);
          await this.loadInvoiceXpressDocuments();
          // Close edit modal if open
          this.invoiceXpress.showEditClientModal = false;
        } else {
          this.toastStore.addToast(
            data.error || 'Error updating draft',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error updating draft:', error);
        // Show the actual error message from InvoiceXpress if available
        const errorMessage = error.message || 'Error updating draft invoice';
        this.toastStore.addToast(errorMessage, 'danger');
      }
    },

    async openEditClientModal(documentId) {
      this.invoiceXpress.editingDocumentId = documentId;
      // Load countries if not already loaded
      if (this.countries.length === 0) {
        await this.fetchCountries();
      }
      // Initialize with current client data from main guest if not already set
      this.initInvoiceXpressClientData();
      this.invoiceXpress.showEditClientModal = true;
    },

    closeEditClientModal() {
      this.invoiceXpress.showEditClientModal = false;
      this.invoiceXpress.editingDocumentId = null;
    },

    async saveClientAndUpdateDraft() {
      const documentId = this.invoiceXpress.editingDocumentId;
      if (!documentId) return;

      this.invoiceXpress.isUpdatingClient = true;
      try {
        await this.updateDraftInvoice(documentId, true);
      } finally {
        this.invoiceXpress.isUpdatingClient = false;
      }
    },

    async issueInvoice(documentId) {
      if (
        !confirm(
          'Are you sure you want to issue this invoice? Once issued, it cannot be modified.',
        )
      ) {
        return;
      }
      this.invoiceXpress.isIssuingInvoice = true;
      try {
        const response = await fetch(`/invoicexpress/issue/${documentId}/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
        });
        const data = await response.json();
        if (data.success) {
          this.toastStore.addToast('Invoice issued successfully', 'success');
          await this.pollInvoiceXpressTaskStatus(data.task_id);
          await this.loadInvoiceXpressDocuments();
          // Refresh staleness check
          this.checkInvoiceStaleness();
        } else {
          this.toastStore.addToast(
            data.error || 'Error issuing invoice',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error issuing invoice:', error);
        this.toastStore.addToast('Error issuing invoice', 'danger');
      } finally {
        this.invoiceXpress.isIssuingInvoice = false;
      }
    },

    openCreditNoteModal(documentId) {
      this.invoiceXpress.selectedDocumentId = documentId;
      this.invoiceXpress.creditNoteReason = '';
      this.invoiceXpress.showCreditNoteModal = true;
    },

    closeCreditNoteModal() {
      this.invoiceXpress.showCreditNoteModal = false;
      this.invoiceXpress.selectedDocumentId = null;
      this.invoiceXpress.creditNoteReason = '';
    },

    async createCreditNote() {
      if (!this.invoiceXpress.creditNoteReason.trim()) {
        this.toastStore.addToast(
          'Please enter a reason for the credit note',
          'warning',
        );
        return;
      }
      this.invoiceXpress.isCreatingCreditNote = true;
      try {
        const response = await fetch(
          `/invoicexpress/credit-note/${this.invoiceXpress.selectedDocumentId}/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              reason: this.invoiceXpress.creditNoteReason,
            }),
          },
        );
        const data = await response.json();
        if (data.success) {
          this.toastStore.addToast(
            'Credit note created successfully',
            'success',
          );
          this.closeCreditNoteModal();
          await this.pollInvoiceXpressTaskStatus(data.task_id);
          await this.loadInvoiceXpressDocuments();
          // Refresh staleness check
          this.checkInvoiceStaleness();
        } else {
          this.toastStore.addToast(
            data.error || 'Error creating credit note',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error creating credit note:', error);
        this.toastStore.addToast('Error creating credit note', 'danger');
      } finally {
        this.invoiceXpress.isCreatingCreditNote = false;
      }
    },

    openReissueModal(documentId) {
      this.invoiceXpress.selectedDocumentId = documentId;
      this.invoiceXpress.reissueReason = '';
      this.invoiceXpress.showReissueModal = true;
    },

    closeReissueModal() {
      this.invoiceXpress.showReissueModal = false;
      this.invoiceXpress.selectedDocumentId = null;
      this.invoiceXpress.reissueReason = '';
    },

    async reissueInvoice() {
      if (!this.invoiceXpress.reissueReason.trim()) {
        this.toastStore.addToast(
          'Please enter a reason for re-issuing the invoice',
          'warning',
        );
        return;
      }
      this.invoiceXpress.isReissuingInvoice = true;
      try {
        const response = await fetch(
          `/invoicexpress/reissue/${this.invoiceXpress.selectedDocumentId}/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({ reason: this.invoiceXpress.reissueReason }),
          },
        );
        const data = await response.json();
        if (data.success) {
          this.toastStore.addToast('Invoice re-issued successfully', 'success');
          this.closeReissueModal();
          await this.pollInvoiceXpressTaskStatus(data.task_id);
          await this.loadInvoiceXpressDocuments();
          // Refresh staleness check
          this.checkInvoiceStaleness();
        } else {
          this.toastStore.addToast(
            data.error || 'Error re-issuing invoice',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error re-issuing invoice:', error);
        this.toastStore.addToast('Error re-issuing invoice', 'danger');
      } finally {
        this.invoiceXpress.isReissuingInvoice = false;
      }
    },

    openVoidInvoiceModal(documentId) {
      this.invoiceXpress.voidingDocumentId = documentId;
      this.invoiceXpress.voidReason = '';
      this.invoiceXpress.showVoidInvoiceModal = true;
    },

    closeVoidInvoiceModal() {
      this.invoiceXpress.showVoidInvoiceModal = false;
      this.invoiceXpress.voidingDocumentId = null;
      this.invoiceXpress.voidReason = '';
    },

    async voidInvoice() {
      if (!this.invoiceXpress.voidReason.trim()) {
        this.toastStore.addToast(
          'Please enter a reason for voiding the invoice',
          'warning',
        );
        return;
      }
      this.invoiceXpress.isVoidingInvoice = true;
      try {
        // Use the existing credit note endpoint - it creates a credit note which effectively voids the invoice
        const response = await fetch(
          `/invoicexpress/credit-note/${this.invoiceXpress.voidingDocumentId}/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({ reason: this.invoiceXpress.voidReason }),
          },
        );
        const data = await response.json();
        if (data.success) {
          this.toastStore.addToast(
            'Invoice voided successfully. Credit note created.',
            'success',
          );
          this.closeVoidInvoiceModal();
          await this.pollInvoiceXpressTaskStatus(data.task_id);
          await this.loadInvoiceXpressDocuments();
          // Refresh staleness check
          this.checkInvoiceStaleness();
        } else {
          this.toastStore.addToast(
            data.error || 'Error voiding invoice',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error voiding invoice:', error);
        this.toastStore.addToast('Error voiding invoice', 'danger');
      } finally {
        this.invoiceXpress.isVoidingInvoice = false;
      }
    },

    async downloadInvoiceXpressPdf(documentId) {
      try {
        this.toastStore.addToast('Fetching PDF...', 'info');
        const response = await fetch(`/invoicexpress/pdf/${documentId}/`);
        const data = await response.json();
        if (data.success && data.pdf_url) {
          window.open(data.pdf_url, '_blank');
        } else {
          this.toastStore.addToast(
            data.message ||
              'PDF not available yet. Try again in a few seconds.',
            'warning',
          );
        }
      } catch (error) {
        console.error('Error getting PDF:', error);
        this.toastStore.addToast('Error getting PDF', 'danger');
      }
    },

    async pollInvoiceXpressTaskStatus(taskId, maxAttempts = 30) {
      for (let i = 0; i < maxAttempts; i++) {
        try {
          const response = await fetch(`/invoicexpress/task-status/${taskId}/`);
          const data = await response.json();
          if (data.status === 'completed') {
            return data;
          } else if (data.status === 'failed') {
            throw new Error(data.message || 'Task failed');
          }
          await new Promise((resolve) => setTimeout(resolve, 2000));
        } catch (error) {
          console.error('Error polling task:', error);
          throw error;
        }
      }
      throw new Error('Task timeout');
    },

    canIssueInvoiceXpress() {
      const status = this.bookingData.status;
      return ['CONFIRMED', 'CONFIRMED_PART'].includes(status);
    },

    getInvoiceXpressStatusBadgeClass(status) {
      const classes = {
        draft: 'bg-warning',
        issued: 'bg-success',
        credited: 'bg-secondary',
        cancelled: 'bg-danger',
      };
      return classes[status] || 'bg-secondary';
    },

    getInvoiceXpressDocumentTypeIcon(type) {
      const icons = {
        draft_invoice: 'fa-file-invoice',
        invoice: 'fa-file-invoice-dollar',
        credit_note: 'fa-file-circle-minus',
      };
      return icons[type] || 'fa-file';
    },

    hasInvoiceXpressDraft() {
      return this.invoiceXpress.documents.some((d) => d.status === 'draft');
    },

    // Check if there's a partner draft (not issued yet)
    hasPartnerDraft() {
      return this.invoiceXpress.documents.some(
        (d) => d.status === 'draft' && d.is_partner_invoice === true,
      );
    },

    // Check if there's a customer draft (not issued yet)
    hasCustomerDraft() {
      return this.invoiceXpress.documents.some(
        (d) => d.status === 'draft' && d.is_partner_invoice !== true,
      );
    },

    // Check if there's an issued partner invoice
    hasIssuedPartnerInvoice() {
      return this.invoiceXpress.documents.some(
        (d) =>
          d.is_issued &&
          d.is_partner_invoice === true &&
          ['invoice', 'draft_invoice'].includes(d.document_type),
      );
    },

    // Check if there's an issued customer invoice
    hasIssuedCustomerInvoice() {
      return this.invoiceXpress.documents.some(
        (d) =>
          d.is_issued &&
          d.is_partner_invoice !== true &&
          ['invoice', 'draft_invoice'].includes(d.document_type),
      );
    },

    // Check if partner invoice is needed (not yet created)
    needsPartnerInvoice() {
      const partner = this.bookingData?.partner;
      if (!partner || partner.invoicing_mode !== 'PARTNER') return false;
      return !this.hasPartnerDraft() && !this.hasIssuedPartnerInvoice();
    },

    // Can create a new invoice (one invoice per booking - either partner or customer)
    canCreateNewInvoice() {
      return (
        !this.hasInvoiceXpressDraft() && !this.hasIssuedInvoiceInDocuments()
      );
    },

    hasIssuedInvoiceInDocuments() {
      return this.invoiceXpress.documents.some(
        (d) =>
          d.is_issued && ['invoice', 'draft_invoice'].includes(d.document_type),
      );
    },

    hasStaleInvoiceInDocuments() {
      return this.invoiceXpress.documents.some(
        (d) =>
          d.is_issued &&
          d.has_booking_changed &&
          d.document_type !== 'credit_note',
      );
    },

    getStaleInvoiceFromDocuments() {
      return this.invoiceXpress.documents.find(
        (d) =>
          d.is_issued &&
          d.has_booking_changed &&
          d.document_type !== 'credit_note',
      );
    },

    resetInvoiceXpressState() {
      this.invoiceXpress.invoiceType = null;
      this.invoiceXpress.hasCheckedEnabled = false;
      this.invoiceXpress.showCreateFormForRemainingInvoice = false;
    },

    // Waiver functionality
    toggleWaiverModal() {
      this.showWaiverModal = !this.showWaiverModal;
      if (this.showWaiverModal) {
        this.waiverData.isGenerating = false;
        this.waiverData.isSendingDigitalWaiver = false;
      }
    },

    // Change Log functionality
    toggleChangeLogModal() {
      this.showChangeLogModal = !this.showChangeLogModal;
    },

    async fetchEmailTemplates() {
      if (!this.bookingData.booking_id) return;
      try {
        const response = await fetch(
          `/fullbooking/email-templates/${this.bookingData.booking_id}/`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );
        if (!response.ok) throw new Error('Failed to load templates');
        const data = await response.json();
        this.emailTemplates = data.templates || [];
      } catch (error) {
        console.error('Error loading email templates:', error);
        this.emailTemplates = [];
      }
    },

    async fetchEmailHistory() {
      if (!this.bookingData.booking_id) return;
      this.loadingEmailHistory = true;
      try {
        const response = await fetch(
          `/fullbooking/email-history/${this.bookingData.booking_id}/`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );
        if (!response.ok) throw new Error('Failed to load email history');
        const data = await response.json();
        this.emailHistory = data.deliveries || [];
      } catch (error) {
        console.error('Error loading email history:', error);
        this.emailHistory = [];
      } finally {
        this.loadingEmailHistory = false;
      }
    },

    toggleSendEmailModal() {
      this.showSendEmailModal = !this.showSendEmailModal;
      if (this.showSendEmailModal) {
        this.fetchEmailTemplates();
      }
    },

    async sendSelectedTemplateEmail() {
      if (!this.selectedTemplateId || this.sendingTemplateEmail) return;
      this.sendingTemplateEmail = true;
      try {
        const response = await fetch(
          `/fullbooking/send-template-email/${this.bookingData.booking_id}/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              template_id: this.selectedTemplateId,
            }),
          },
        );
        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.error || 'Failed to send email');
        }
        this.showSendEmailModal = false;
        this.selectedTemplateId = '';
        await this.fetchEmailHistory();
        if (this.toastStore && typeof this.toastStore.show === 'function') {
          this.toastStore.show('Email sent successfully.', 'success');
        }
      } catch (error) {
        console.error('Error sending manual template email:', error);
        if (this.toastStore && typeof this.toastStore.show === 'function') {
          this.toastStore.show(
            error.message || 'Failed to send email.',
            'error',
          );
        }
      } finally {
        this.sendingTemplateEmail = false;
      }
    },

    async generateWaiver() {
      this.waiverData.isGenerating = true;

      try {
        const response = await fetch(
          `/fullbooking/generate-waiver/${this.bookingData.booking_id}/`,
          {
            method: 'POST',
            headers: {
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );

        const data = await response.json();

        if (response.ok && data.success && data.task_id) {
          // Task queued successfully, start polling
          if (this.toastStore && this.toastStore.addToast) {
            this.toastStore.addToast(
              'Waiver generation is processing in the background. This may take a few moments.',
              'info',
            );
          }
          // Start polling for task status
          this.pollWaiverStatus(data.task_id);
        } else {
          // Error queueing task
          if (this.toastStore && this.toastStore.addToast) {
            this.toastStore.addToast(
              data.message || 'Error queueing waiver generation',
              'danger',
            );
          }
          this.waiverData.isGenerating = false;
        }
      } catch (error) {
        console.error('Error queueing waiver generation:', error);
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            'Error queueing waiver generation',
            'danger',
          );
        }
        this.waiverData.isGenerating = false;
      }
    },

    async sendDigitalWaiver() {
      if (this.waiverData.isSendingDigitalWaiver) return;
      this.waiverData.isSendingDigitalWaiver = true;
      try {
        const response = await fetch(
          `/fullbooking/send-digital-waiver/${this.bookingData.booking_id}/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.error || 'Failed to send digital waiver');
        }
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast('Digital waiver email sent', 'success');
        }
        await this.fetchBookingData(this.bookingData.booking_id);
      } catch (error) {
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            error.message || 'Failed to send digital waiver',
            'danger',
          );
        }
      } finally {
        this.waiverData.isSendingDigitalWaiver = false;
      }
    },

    async pollWaiverStatus(taskId) {
      const maxAttempts = 120; // Poll for up to 10 minutes (5 sec intervals)
      let attempts = 0;

      const checkStatus = async () => {
        try {
          const response = await fetch(
            `/fullbooking/waiver-status/${taskId}/`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            },
          );

          const data = await response.json();
          console.log('Waiver task status:', data);

          if (data.status === 'completed') {
            if (data.success) {
              // Download the PDF
              const downloadResponse = await fetch(
                `/fullbooking/waiver-download/${taskId}/`,
                {
                  method: 'GET',
                },
              );

              if (downloadResponse.ok) {
                // Get the filename from the response headers
                const contentDisposition = downloadResponse.headers.get(
                  'Content-Disposition',
                );
                let filename = data.filename || 'waiver.pdf';
                if (contentDisposition) {
                  const filenameMatch =
                    contentDisposition.match(/filename="(.+)"/);
                  if (filenameMatch) {
                    filename = filenameMatch[1];
                  }
                }

                // Create a blob and download the file
                const blob = await downloadResponse.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                // Close the modal and show success message
                this.showWaiverModal = false;
                if (this.toastStore && this.toastStore.addToast) {
                  this.toastStore.addToast(
                    'Waiver generated successfully',
                    'success',
                  );
                }
              } else {
                if (this.toastStore && this.toastStore.addToast) {
                  this.toastStore.addToast(
                    'Error downloading waiver',
                    'danger',
                  );
                }
              }
            } else {
              if (this.toastStore && this.toastStore.addToast) {
                this.toastStore.addToast(
                  `Waiver generation failed: ${data.message}`,
                  'error',
                );
              }
            }
            this.waiverData.isGenerating = false;
            return; // Stop polling
          } else if (data.status === 'failed') {
            if (this.toastStore && this.toastStore.addToast) {
              this.toastStore.addToast(
                `Waiver generation failed: ${data.message}`,
                'danger',
              );
            }
            this.waiverData.isGenerating = false;
            return; // Stop polling
          } else if (
            data.status === 'pending' ||
            data.status === 'in_progress'
          ) {
            // Task is still processing, continue polling
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(checkStatus, 5000); // Poll every 5 seconds
            } else {
              if (this.toastStore && this.toastStore.addToast) {
                this.toastStore.addToast(
                  'Waiver generation is taking longer than expected. Please try again later.',
                  'warning',
                );
              }
              this.waiverData.isGenerating = false;
            }
          }
        } catch (error) {
          console.error('Error checking waiver task status:', error);
          // Continue polling despite errors, but limit attempts
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 5000);
          } else {
            this.waiverData.isGenerating = false;
          }
        }
      };

      // Start polling
      checkStatus();
    },

    // Helper function to get human-readable document type
    getDocumentTypeDisplay(documentType) {
      const documentTypeMap = {
        DRIVERS_LICENCE: "Driver's Licence",
        ID: 'ID Card',
        PASSPORT: 'Passport',
        '': '',
      };
      return documentTypeMap[documentType] || documentType;
    },

    // =====================================================================
    // MANUAL DISCOUNT METHODS
    // =====================================================================

    showDiscountModal: false,
    discountModalTarget: 'booking', // 'booking', 'item', 'extra'
    discountModalTargetId: null,
    discountModalError: '',
    isApplyingDiscount: false,
    availableDiscounts: [],
    selectedDiscountId: null,
    discountMode: 'existing', // 'existing' or 'custom'
    isLoadingDiscounts: false,
    discountForm: {
      name: '',
      value_type: 'PERCENTAGE',
      amount: '',
    },

    async openManualDiscountModal(targetType, targetId = null) {
      this.discountModalTarget = targetType;
      this.discountModalTargetId = targetId;
      this.discountModalError = '';
      this.selectedDiscountId = null;
      this.discountMode = 'existing';
      this.discountForm = {
        name: '',
        value_type: 'PERCENTAGE',
        amount: '',
      };
      this.showDiscountModal = true;
      await this.fetchAvailableDiscounts();
    },

    async fetchAvailableDiscounts() {
      this.isLoadingDiscounts = true;
      try {
        const response = await fetch(
          `/bookings_app/list-manual-discounts/${this.bookingData.booking_id}/`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );
        const data = await response.json();
        if (response.ok && data.success) {
          this.availableDiscounts = data.discounts;
          // If no existing discounts, default to custom mode
          if (this.availableDiscounts.length === 0) {
            this.discountMode = 'custom';
          }
        } else {
          this.availableDiscounts = [];
          this.discountMode = 'custom';
        }
      } catch (error) {
        console.error('Error fetching available discounts:', error);
        this.availableDiscounts = [];
        this.discountMode = 'custom';
      } finally {
        this.isLoadingDiscounts = false;
      }
    },

    closeDiscountModal() {
      this.showDiscountModal = false;
      this.discountModalTarget = 'booking';
      this.discountModalTargetId = null;
      this.discountModalError = '';
      this.selectedDiscountId = null;
      this.availableDiscounts = [];
    },

    async applyManualDiscount() {
      // Build request body based on mode
      let body = {
        booking_id: this.bookingData.booking_id,
        target_type: this.discountModalTarget,
        target_id: this.discountModalTargetId,
      };

      if (this.discountMode === 'existing') {
        if (!this.selectedDiscountId) {
          this.discountModalError = 'Please select a discount';
          return;
        }
        body.discount_id = this.selectedDiscountId;
      } else {
        // Custom mode
        if (!this.discountForm.name.trim()) {
          this.discountModalError = 'Please enter a discount name';
          return;
        }
        if (
          !this.discountForm.amount ||
          parseFloat(this.discountForm.amount) <= 0
        ) {
          this.discountModalError = 'Please enter a valid discount amount';
          return;
        }
        if (
          this.discountForm.value_type === 'PERCENTAGE' &&
          parseFloat(this.discountForm.amount) > 100
        ) {
          this.discountModalError = 'Percentage cannot exceed 100%';
          return;
        }
        body.discount_name = this.discountForm.name;
        body.value_type = this.discountForm.value_type;
        body.amount = parseFloat(this.discountForm.amount);
      }

      this.isApplyingDiscount = true;
      this.discountModalError = '';

      try {
        const response = await fetch('/bookings_app/apply-manual-discount/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(body),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          this.closeDiscountModal();
          // Refresh booking data
          await this.fetchBookingData(this.bookingData.booking_id);
          if (this.toastStore && typeof this.toastStore.show === 'function') {
            this.toastStore.show(
              `Discount "${data.discount_name}" applied (-${data.total_saved})`,
              'success',
            );
          }
        } else {
          this.discountModalError = data.error || 'Failed to apply discount';
        }
      } catch (error) {
        console.error('Error applying manual discount:', error);
        this.discountModalError = 'An error occurred. Please try again.';
      } finally {
        this.isApplyingDiscount = false;
      }
    },

    async removeManualDiscount(discountId, targetType = null, targetId = null) {
      if (!confirm('Are you sure you want to remove this discount?')) {
        return;
      }
      try {
        const body = {
          booking_id: this.bookingData.booking_id,
          discount_id: discountId,
        };

        // If target specified, remove from specific item/extra only
        if (targetType && targetId) {
          body.target_type = targetType;
          body.target_id = targetId;
        }

        const response = await fetch('/bookings_app/remove-manual-discount/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(body),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // Refresh booking data
          await this.fetchBookingData(this.bookingData.booking_id);
          if (this.toastStore && typeof this.toastStore.show === 'function') {
            this.toastStore.show('Discount removed', 'success');
          }
        }
      } catch (error) {
        console.error('Error removing manual discount:', error);
      }
    },

    /**
     * Return a plain array of manual discount entries for a booking item or extra.
     * Uses amount_saved from price_breakdown when available so displayed amounts
     * match the breakdown (net for partner bookings).
     */
    getManualDiscountsList(record) {
      if (!record || !record.applied_discounts) return [];
      const raw = record.applied_discounts;
      const list = Array.isArray(raw) ? raw : [];
      const manual = list.filter((d) => d && d.application_mode === 'MANUAL');
      const fromBreakdown = record.price_breakdown?.applied_discounts;
      if (!Array.isArray(fromBreakdown)) return manual;
      return manual.map((d) => {
        const match = fromBreakdown.find(
          (b) => b && String(b.discount_id) === String(d.discount_id),
        );
        return match && match.amount_saved != null
          ? { ...d, amount_saved: match.amount_saved }
          : d;
      });
    },

    getAppliedManualDiscounts() {
      // Aggregate all manual discounts across items and extras
      const discountMap = {};
      const items = this.bookingData.booking_items || [];
      const extras = this.bookingData.booking_extras || [];

      for (const item of items) {
        for (const d of item.applied_discounts || []) {
          if (d.application_mode === 'MANUAL') {
            if (!discountMap[d.discount_id]) {
              discountMap[d.discount_id] = {
                discount_id: d.discount_id,
                name: d.name,
                total_saved: 0,
              };
            }
            discountMap[d.discount_id].total_saved += parseFloat(
              d.amount_saved || 0,
            );
          }
        }
      }

      for (const extra of extras) {
        for (const d of extra.applied_discounts || []) {
          if (d.application_mode === 'MANUAL') {
            if (!discountMap[d.discount_id]) {
              discountMap[d.discount_id] = {
                discount_id: d.discount_id,
                name: d.name,
                total_saved: 0,
              };
            }
            discountMap[d.discount_id].total_saved += parseFloat(
              d.amount_saved || 0,
            );
          }
        }
      }

      return Object.values(discountMap);
    },

    getTotalManualDiscounts() {
      return this.getAppliedManualDiscounts().reduce(
        (sum, d) => sum + d.total_saved,
        0,
      );
    },
  });
}
